import { useAtom, atom } from 'jotai';
import Cookies from 'js-cookie';
import { useEffect, useMemo } from 'react';
import { NETWORK, sToMs, numberWithCommas, V3, calculateLPTokenPrice, SECONDS_PER_DAY, amountMultByUsd, getAmountFromUnformatted, toScaledUsdBigNumber, GOVERNANCE_CONTRACT_ADDRESSES, formatUnformattedBigNumberForDisplay } from '@pooltogether/utilities';
import { formatUnits, parseUnits } from '@ethersproject/units';
import { contract, batch } from '@pooltogether/etherplex';
import { getReadProvider, getReadProviders, useReadProvider } from '@pooltogether/wallet-connection';
import { useQueryClient, useQuery, useQueries } from 'react-query';
import { isAddress, getAddress } from '@ethersproject/address';
import { BigNumber, ethers } from 'ethers';
import { request } from 'graphql-request';
import gql from 'graphql-tag';

var domainAtom = /*#__PURE__*/atom('');
var useCookieOptions = function useCookieOptions() {
  var _useAtom = useAtom(domainAtom),
      domain = _useAtom[0];

  return {
    sameSite: 'strict',
    secure: domain === 'pooltogether.com',
    domain: domain && "." + domain
  };
};
/**
 * Call once
 * @param domain
 */

var useInitCookieOptions = function useInitCookieOptions(domain) {
  var _useAtom2 = useAtom(domainAtom),
      setDomain = _useAtom2[1];

  useEffect(function () {
    setDomain(domain);
  }, [domain]);
};

var APP_ENVIRONMENT_KEY = '_useTestnets';
var APP_ENVIRONMENTS = {
  testnets: 'testnets',
  mainnets: 'mainnets'
};
var getStoredIsTestnetsCookie = function getStoredIsTestnetsCookie() {
  return Boolean(Cookies.get(APP_ENVIRONMENT_KEY));
};
/**
 * If there's something stored then we want testnets
 */

var isTestnetsAtom = /*#__PURE__*/atom( /*#__PURE__*/getStoredIsTestnetsCookie());
/**
 * Used to manage what pools we want to display to the user.
 * Determines the current environment based on a cookie & the users wallet.
 * Controlled by a toggle in settings. Toggle was chosen over watching a users
 * wallet for simplicity around all of the edge cases.
 * ex. a user loads /pools/goerli but with their wallet connected to BSC.
 * @returns object
 */

var useIsTestnets = function useIsTestnets() {
  var _useAtom = useAtom(isTestnetsAtom),
      isTestnets = _useAtom[0],
      setIsTestnetsState = _useAtom[1];

  var cookieOptions = useCookieOptions();

  var enableTestnets = function enableTestnets() {
    var cookieValue = 'true';
    Cookies.set(APP_ENVIRONMENT_KEY, cookieValue, cookieOptions);
    setIsTestnetsState(true);
  };

  var disableTestnets = function disableTestnets() {
    Cookies.remove(APP_ENVIRONMENT_KEY, cookieOptions);
    setIsTestnetsState(false);
  };

  return {
    isTestnets: isTestnets,
    enableTestnets: enableTestnets,
    disableTestnets: disableTestnets
  };
};

var getAppEnvString = function getAppEnvString() {
  var isTestnets = getStoredIsTestnetsCookie();
  return isTestnets ? APP_ENVIRONMENTS.testnets : APP_ENVIRONMENTS.mainnets;
};

var theGraphApiKeyAtom = /*#__PURE__*/atom('');
var useInitTheGraphApiKey = function useInitTheGraphApiKey(graphApiKey) {
  var _useAtom2 = useAtom(theGraphApiKeyAtom),
      setTheGraphApiKey = _useAtom2[1];

  useEffect(function () {
    setTheGraphApiKey(graphApiKey);
  }, [graphApiKey]);
};

var _Object$freeze;
var REFETCH_INTERVALS = /*#__PURE__*/Object.freeze((_Object$freeze = {}, _Object$freeze[NETWORK.mainnet] = /*#__PURE__*/sToMs(16), _Object$freeze[NETWORK.polygon] = /*#__PURE__*/sToMs(5), _Object$freeze[NETWORK.mumbai] = /*#__PURE__*/sToMs(5), _Object$freeze[NETWORK.avalanche] = /*#__PURE__*/sToMs(5), _Object$freeze[NETWORK.fuji] = /*#__PURE__*/sToMs(5), _Object$freeze));
var useRefetchInterval = function useRefetchInterval(chainId) {
  if (chainId === void 0) {
    chainId = NETWORK.mainnet;
  }

  return getRefetchInterval(chainId);
};
var getRefetchInterval = function getRefetchInterval(chainId) {
  if (chainId === void 0) {
    chainId = NETWORK.mainnet;
  }

  return REFETCH_INTERVALS[chainId] || REFETCH_INTERVALS[NETWORK.mainnet];
};

var useGovernanceChainId = function useGovernanceChainId() {
  var _useIsTestnets = useIsTestnets(),
      isTestnets = _useIsTestnets.isTestnets;

  return isTestnets ? NETWORK.goerli : NETWORK.mainnet;
};

function _regeneratorRuntime() {
  /*! regenerator-runtime -- Copyright (c) 2014-present, Facebook, Inc. -- license (MIT): https://github.com/facebook/regenerator/blob/main/LICENSE */

  _regeneratorRuntime = function () {
    return exports;
  };

  var exports = {},
      Op = Object.prototype,
      hasOwn = Op.hasOwnProperty,
      $Symbol = "function" == typeof Symbol ? Symbol : {},
      iteratorSymbol = $Symbol.iterator || "@@iterator",
      asyncIteratorSymbol = $Symbol.asyncIterator || "@@asyncIterator",
      toStringTagSymbol = $Symbol.toStringTag || "@@toStringTag";

  function define(obj, key, value) {
    return Object.defineProperty(obj, key, {
      value: value,
      enumerable: !0,
      configurable: !0,
      writable: !0
    }), obj[key];
  }

  try {
    define({}, "");
  } catch (err) {
    define = function (obj, key, value) {
      return obj[key] = value;
    };
  }

  function wrap(innerFn, outerFn, self, tryLocsList) {
    var protoGenerator = outerFn && outerFn.prototype instanceof Generator ? outerFn : Generator,
        generator = Object.create(protoGenerator.prototype),
        context = new Context(tryLocsList || []);
    return generator._invoke = function (innerFn, self, context) {
      var state = "suspendedStart";
      return function (method, arg) {
        if ("executing" === state) throw new Error("Generator is already running");

        if ("completed" === state) {
          if ("throw" === method) throw arg;
          return doneResult();
        }

        for (context.method = method, context.arg = arg;;) {
          var delegate = context.delegate;

          if (delegate) {
            var delegateResult = maybeInvokeDelegate(delegate, context);

            if (delegateResult) {
              if (delegateResult === ContinueSentinel) continue;
              return delegateResult;
            }
          }

          if ("next" === context.method) context.sent = context._sent = context.arg;else if ("throw" === context.method) {
            if ("suspendedStart" === state) throw state = "completed", context.arg;
            context.dispatchException(context.arg);
          } else "return" === context.method && context.abrupt("return", context.arg);
          state = "executing";
          var record = tryCatch(innerFn, self, context);

          if ("normal" === record.type) {
            if (state = context.done ? "completed" : "suspendedYield", record.arg === ContinueSentinel) continue;
            return {
              value: record.arg,
              done: context.done
            };
          }

          "throw" === record.type && (state = "completed", context.method = "throw", context.arg = record.arg);
        }
      };
    }(innerFn, self, context), generator;
  }

  function tryCatch(fn, obj, arg) {
    try {
      return {
        type: "normal",
        arg: fn.call(obj, arg)
      };
    } catch (err) {
      return {
        type: "throw",
        arg: err
      };
    }
  }

  exports.wrap = wrap;
  var ContinueSentinel = {};

  function Generator() {}

  function GeneratorFunction() {}

  function GeneratorFunctionPrototype() {}

  var IteratorPrototype = {};
  define(IteratorPrototype, iteratorSymbol, function () {
    return this;
  });
  var getProto = Object.getPrototypeOf,
      NativeIteratorPrototype = getProto && getProto(getProto(values([])));
  NativeIteratorPrototype && NativeIteratorPrototype !== Op && hasOwn.call(NativeIteratorPrototype, iteratorSymbol) && (IteratorPrototype = NativeIteratorPrototype);
  var Gp = GeneratorFunctionPrototype.prototype = Generator.prototype = Object.create(IteratorPrototype);

  function defineIteratorMethods(prototype) {
    ["next", "throw", "return"].forEach(function (method) {
      define(prototype, method, function (arg) {
        return this._invoke(method, arg);
      });
    });
  }

  function AsyncIterator(generator, PromiseImpl) {
    function invoke(method, arg, resolve, reject) {
      var record = tryCatch(generator[method], generator, arg);

      if ("throw" !== record.type) {
        var result = record.arg,
            value = result.value;
        return value && "object" == typeof value && hasOwn.call(value, "__await") ? PromiseImpl.resolve(value.__await).then(function (value) {
          invoke("next", value, resolve, reject);
        }, function (err) {
          invoke("throw", err, resolve, reject);
        }) : PromiseImpl.resolve(value).then(function (unwrapped) {
          result.value = unwrapped, resolve(result);
        }, function (error) {
          return invoke("throw", error, resolve, reject);
        });
      }

      reject(record.arg);
    }

    var previousPromise;

    this._invoke = function (method, arg) {
      function callInvokeWithMethodAndArg() {
        return new PromiseImpl(function (resolve, reject) {
          invoke(method, arg, resolve, reject);
        });
      }

      return previousPromise = previousPromise ? previousPromise.then(callInvokeWithMethodAndArg, callInvokeWithMethodAndArg) : callInvokeWithMethodAndArg();
    };
  }

  function maybeInvokeDelegate(delegate, context) {
    var method = delegate.iterator[context.method];

    if (undefined === method) {
      if (context.delegate = null, "throw" === context.method) {
        if (delegate.iterator.return && (context.method = "return", context.arg = undefined, maybeInvokeDelegate(delegate, context), "throw" === context.method)) return ContinueSentinel;
        context.method = "throw", context.arg = new TypeError("The iterator does not provide a 'throw' method");
      }

      return ContinueSentinel;
    }

    var record = tryCatch(method, delegate.iterator, context.arg);
    if ("throw" === record.type) return context.method = "throw", context.arg = record.arg, context.delegate = null, ContinueSentinel;
    var info = record.arg;
    return info ? info.done ? (context[delegate.resultName] = info.value, context.next = delegate.nextLoc, "return" !== context.method && (context.method = "next", context.arg = undefined), context.delegate = null, ContinueSentinel) : info : (context.method = "throw", context.arg = new TypeError("iterator result is not an object"), context.delegate = null, ContinueSentinel);
  }

  function pushTryEntry(locs) {
    var entry = {
      tryLoc: locs[0]
    };
    1 in locs && (entry.catchLoc = locs[1]), 2 in locs && (entry.finallyLoc = locs[2], entry.afterLoc = locs[3]), this.tryEntries.push(entry);
  }

  function resetTryEntry(entry) {
    var record = entry.completion || {};
    record.type = "normal", delete record.arg, entry.completion = record;
  }

  function Context(tryLocsList) {
    this.tryEntries = [{
      tryLoc: "root"
    }], tryLocsList.forEach(pushTryEntry, this), this.reset(!0);
  }

  function values(iterable) {
    if (iterable) {
      var iteratorMethod = iterable[iteratorSymbol];
      if (iteratorMethod) return iteratorMethod.call(iterable);
      if ("function" == typeof iterable.next) return iterable;

      if (!isNaN(iterable.length)) {
        var i = -1,
            next = function next() {
          for (; ++i < iterable.length;) if (hasOwn.call(iterable, i)) return next.value = iterable[i], next.done = !1, next;

          return next.value = undefined, next.done = !0, next;
        };

        return next.next = next;
      }
    }

    return {
      next: doneResult
    };
  }

  function doneResult() {
    return {
      value: undefined,
      done: !0
    };
  }

  return GeneratorFunction.prototype = GeneratorFunctionPrototype, define(Gp, "constructor", GeneratorFunctionPrototype), define(GeneratorFunctionPrototype, "constructor", GeneratorFunction), GeneratorFunction.displayName = define(GeneratorFunctionPrototype, toStringTagSymbol, "GeneratorFunction"), exports.isGeneratorFunction = function (genFun) {
    var ctor = "function" == typeof genFun && genFun.constructor;
    return !!ctor && (ctor === GeneratorFunction || "GeneratorFunction" === (ctor.displayName || ctor.name));
  }, exports.mark = function (genFun) {
    return Object.setPrototypeOf ? Object.setPrototypeOf(genFun, GeneratorFunctionPrototype) : (genFun.__proto__ = GeneratorFunctionPrototype, define(genFun, toStringTagSymbol, "GeneratorFunction")), genFun.prototype = Object.create(Gp), genFun;
  }, exports.awrap = function (arg) {
    return {
      __await: arg
    };
  }, defineIteratorMethods(AsyncIterator.prototype), define(AsyncIterator.prototype, asyncIteratorSymbol, function () {
    return this;
  }), exports.AsyncIterator = AsyncIterator, exports.async = function (innerFn, outerFn, self, tryLocsList, PromiseImpl) {
    void 0 === PromiseImpl && (PromiseImpl = Promise);
    var iter = new AsyncIterator(wrap(innerFn, outerFn, self, tryLocsList), PromiseImpl);
    return exports.isGeneratorFunction(outerFn) ? iter : iter.next().then(function (result) {
      return result.done ? result.value : iter.next();
    });
  }, defineIteratorMethods(Gp), define(Gp, toStringTagSymbol, "Generator"), define(Gp, iteratorSymbol, function () {
    return this;
  }), define(Gp, "toString", function () {
    return "[object Generator]";
  }), exports.keys = function (object) {
    var keys = [];

    for (var key in object) keys.push(key);

    return keys.reverse(), function next() {
      for (; keys.length;) {
        var key = keys.pop();
        if (key in object) return next.value = key, next.done = !1, next;
      }

      return next.done = !0, next;
    };
  }, exports.values = values, Context.prototype = {
    constructor: Context,
    reset: function (skipTempReset) {
      if (this.prev = 0, this.next = 0, this.sent = this._sent = undefined, this.done = !1, this.delegate = null, this.method = "next", this.arg = undefined, this.tryEntries.forEach(resetTryEntry), !skipTempReset) for (var name in this) "t" === name.charAt(0) && hasOwn.call(this, name) && !isNaN(+name.slice(1)) && (this[name] = undefined);
    },
    stop: function () {
      this.done = !0;
      var rootRecord = this.tryEntries[0].completion;
      if ("throw" === rootRecord.type) throw rootRecord.arg;
      return this.rval;
    },
    dispatchException: function (exception) {
      if (this.done) throw exception;
      var context = this;

      function handle(loc, caught) {
        return record.type = "throw", record.arg = exception, context.next = loc, caught && (context.method = "next", context.arg = undefined), !!caught;
      }

      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i],
            record = entry.completion;
        if ("root" === entry.tryLoc) return handle("end");

        if (entry.tryLoc <= this.prev) {
          var hasCatch = hasOwn.call(entry, "catchLoc"),
              hasFinally = hasOwn.call(entry, "finallyLoc");

          if (hasCatch && hasFinally) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          } else if (hasCatch) {
            if (this.prev < entry.catchLoc) return handle(entry.catchLoc, !0);
          } else {
            if (!hasFinally) throw new Error("try statement without catch or finally");
            if (this.prev < entry.finallyLoc) return handle(entry.finallyLoc);
          }
        }
      }
    },
    abrupt: function (type, arg) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc <= this.prev && hasOwn.call(entry, "finallyLoc") && this.prev < entry.finallyLoc) {
          var finallyEntry = entry;
          break;
        }
      }

      finallyEntry && ("break" === type || "continue" === type) && finallyEntry.tryLoc <= arg && arg <= finallyEntry.finallyLoc && (finallyEntry = null);
      var record = finallyEntry ? finallyEntry.completion : {};
      return record.type = type, record.arg = arg, finallyEntry ? (this.method = "next", this.next = finallyEntry.finallyLoc, ContinueSentinel) : this.complete(record);
    },
    complete: function (record, afterLoc) {
      if ("throw" === record.type) throw record.arg;
      return "break" === record.type || "continue" === record.type ? this.next = record.arg : "return" === record.type ? (this.rval = this.arg = record.arg, this.method = "return", this.next = "end") : "normal" === record.type && afterLoc && (this.next = afterLoc), ContinueSentinel;
    },
    finish: function (finallyLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];
        if (entry.finallyLoc === finallyLoc) return this.complete(entry.completion, entry.afterLoc), resetTryEntry(entry), ContinueSentinel;
      }
    },
    catch: function (tryLoc) {
      for (var i = this.tryEntries.length - 1; i >= 0; --i) {
        var entry = this.tryEntries[i];

        if (entry.tryLoc === tryLoc) {
          var record = entry.completion;

          if ("throw" === record.type) {
            var thrown = record.arg;
            resetTryEntry(entry);
          }

          return thrown;
        }
      }

      throw new Error("illegal catch attempt");
    },
    delegateYield: function (iterable, resultName, nextLoc) {
      return this.delegate = {
        iterator: values(iterable),
        resultName: resultName,
        nextLoc: nextLoc
      }, "next" === this.method && (this.arg = undefined), ContinueSentinel;
    }
  }, exports;
}

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
  try {
    var info = gen[key](arg);
    var value = info.value;
  } catch (error) {
    reject(error);
    return;
  }

  if (info.done) {
    resolve(value);
  } else {
    Promise.resolve(value).then(_next, _throw);
  }
}

function _asyncToGenerator(fn) {
  return function () {
    var self = this,
        args = arguments;
    return new Promise(function (resolve, reject) {
      var gen = fn.apply(self, args);

      function _next(value) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
      }

      function _throw(err) {
        asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
      }

      _next(undefined);
    });
  };
}

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };
  return _extends.apply(this, arguments);
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _taggedTemplateLiteralLoose(strings, raw) {
  if (!raw) {
    raw = strings.slice(0);
  }

  strings.raw = raw;
  return strings;
}

var ERC20Abi = [{
  constant: true,
  inputs: [],
  name: 'name',
  outputs: [{
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'from',
    type: 'address'
  }, {
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'transferFrom',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'decimals',
  outputs: [{
    name: '',
    type: 'uint8'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseAllowance',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'mint',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'owner',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'symbol',
  outputs: [{
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'account',
    type: 'address'
  }],
  name: 'addMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [],
  name: 'renounceMinter',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'spender',
    type: 'address'
  }, {
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseAllowance',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'to',
    type: 'address'
  }, {
    name: 'value',
    type: 'uint256'
  }],
  name: 'transfer',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'account',
    type: 'address'
  }],
  name: 'isMinter',
  outputs: [{
    name: '',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    name: 'sender',
    type: 'address'
  }],
  name: 'initialize',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    name: 'owner',
    type: 'address'
  }, {
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'account',
    type: 'address'
  }],
  name: 'MinterAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'account',
    type: 'address'
  }],
  name: 'MinterRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    name: 'value',
    type: 'uint256'
  }],
  name: 'Transfer',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    name: 'owner',
    type: 'address'
  }, {
    indexed: true,
    name: 'spender',
    type: 'address'
  }, {
    indexed: false,
    name: 'value',
    type: 'uint256'
  }],
  name: 'Approval',
  type: 'event'
}];

var _Object$freeze$1;
var QUERY_KEYS = /*#__PURE__*/Object.freeze({
  claimablePoolFromTokenFaucets: 'claimablePoolFromTokenFaucets',
  getCoingeckoTokenData: 'getCoingeckoTokenData',
  prizePeriod: 'prizePeriod',
  poolTokenDataQuery: 'poolTokenDataQuery',
  readProvider: 'readProvider',
  readProviders: 'readProviders',
  retroactivePoolClaimDataQuery: 'retroactivePoolClaimDataQuery',
  tokenAllowances: 'tokenAllowances',
  tokenBalances: 'tokenBalances',
  tokenHolderQuery: 'tokenHolderQuery',
  usePool: 'usePool',
  usePools: 'usePools',
  usePoolsByChainId: 'usePoolsByChainId',
  userData: 'userData',
  userTicketData: 'userTicketData',
  tokens: 'tokens',
  getCoingeckoTokenPrices: 'getCoingeckoTokenPrices',
  getCoingeckoSimplePrices: 'getCoingeckoSimplePrices',
  usersPodBalance: 'usersPodBalance',
  uniswapLPStakingPool: 'uniswapLPStakingPool',
  getCoingeckoExchangeRates: 'getCoingeckoExchangeRates'
});
var NO_REFETCH = /*#__PURE__*/Object.freeze({
  refetchInterval: false,
  refetchIntervalInBackground: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
  refetchOnWindowFocus: false
}); // CoinGecko

var COINGECKO_API_URL = 'https://api.coingecko.com/api/v3';
var COINGECKO_ASSET_PLATFORMS = /*#__PURE__*/Object.freeze((_Object$freeze$1 = {}, _Object$freeze$1[NETWORK.mainnet] = 'ethereum', _Object$freeze$1[NETWORK.bsc] = 'binance-smart-chain', _Object$freeze$1[NETWORK.polygon] = 'polygon-pos', _Object$freeze$1[NETWORK.celo] = 'celo', _Object$freeze$1[NETWORK.goerli] = 'goerli', _Object$freeze$1[NETWORK.mumbai] = 'mumbai', _Object$freeze$1[NETWORK.avalanche] = 'avalanche', _Object$freeze$1[NETWORK.optimism] = 'optimistic-ethereum', _Object$freeze$1[NETWORK['optimism-kovan']] = 'optimistic-ethereum', _Object$freeze$1[NETWORK['optimism-goerli']] = 'optimistic-ethereum', _Object$freeze$1));
var ProposalStatus;

(function (ProposalStatus) {
  ProposalStatus["pending"] = "pending";
  ProposalStatus["active"] = "active";
  ProposalStatus["cancelled"] = "cancelled";
  ProposalStatus["defeated"] = "defeated";
  ProposalStatus["succeeded"] = "succeeded";
  ProposalStatus["queued"] = "queued";
  ProposalStatus["expired"] = "expired";
  ProposalStatus["executed"] = "executed";
})(ProposalStatus || (ProposalStatus = {})); // Note: Order matches contracts


var PROPOSAL_STATES = [ProposalStatus.pending, ProposalStatus.active, ProposalStatus.cancelled, ProposalStatus.defeated, ProposalStatus.succeeded, ProposalStatus.queued, ProposalStatus.expired, ProposalStatus.executed];

/**
 * Generic function for populating individual caches
 * when querying for lists of data.
 * Ex. Populating the cache for a users token balances for indiviudal
 * tokens after querying for a list of tokens.
 * @param queryClient
 * @param getCacheKey
 * @param data
 * @returns
 */
var populatePerIdCache = function populatePerIdCache(queryClient, getCacheKey, data) {
  var ids = Object.keys(data);
  if (ids.length === 1) return;
  ids.forEach(function (id) {
    var _queryClient$setQuery;

    var datum = data[id];
    queryClient.setQueryData(getCacheKey([id]), (_queryClient$setQuery = {}, _queryClient$setQuery[id] = datum, _queryClient$setQuery));
  });
};

var _excluded = ["data"];
/**
 * Returns a dictionary keyed by the token addresses filled with
 * the provided addresses balances of the provided tokens
 * @param chainId
 * @param address
 * @param tokenAddresses
 * @returns
 */

var useTokenBalances = function useTokenBalances(chainId, address, tokenAddresses, refetchInterval) {
  var queryClient = useQueryClient();
  var enabled = Boolean(address) && tokenAddresses.reduce(function (aggregate, current) {
    return aggregate && Boolean(current);
  }, true) && Array.isArray(tokenAddresses) && tokenAddresses.length > 0 && Boolean(chainId);

  var getCacheKey = function getCacheKey(id) {
    return [QUERY_KEYS.tokenBalances, chainId, address, id];
  };

  return useQuery(getCacheKey(tokenAddresses), /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getTokenBalances(chainId, address, tokenAddresses);

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({
    enabled: enabled,
    refetchInterval: refetchInterval
  }, NO_REFETCH, {
    onSuccess: function onSuccess(data) {
      return populatePerIdCache(queryClient, getCacheKey, data);
    }
  }));
};
/**
 * Returns the provided addresses balance of the provided token
 * @param chainId
 * @param usersAddress
 * @param tokenAddress
 * @returns
 */

var useTokenBalance = function useTokenBalance(chainId, address, tokenAddress, refetchInterval) {
  var _useTokenBalances = useTokenBalances(chainId, address, [tokenAddress], refetchInterval),
      tokenBalances = _useTokenBalances.data,
      queryData = _objectWithoutPropertiesLoose(_useTokenBalances, _excluded);

  return _extends({}, queryData, {
    data: tokenBalances ? tokenBalances[tokenAddress] : null
  });
};
var getTokenBalances = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(chainId, address, tokenAddresses) {
    var batchCalls, readProvider, response, result;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            batchCalls = [];
            tokenAddresses.map(function (tokenAddress) {
              var tokenContract = contract(tokenAddress, ERC20Abi, tokenAddress);
              batchCalls.push(tokenContract.balanceOf(address).decimals().name().symbol().totalSupply());
            });
            readProvider = getReadProvider(chainId);
            _context2.next = 5;
            return batch.apply(void 0, [readProvider].concat(batchCalls));

          case 5:
            response = _context2.sent;
            result = {};
            Object.keys(response).map(function (tokenAddress) {
              var amountUnformatted = response[tokenAddress].balanceOf[0];
              var decimals = response[tokenAddress].decimals[0];
              var amount = formatUnits(amountUnformatted, decimals);
              var amountPretty = numberWithCommas(amount);
              var name = response[tokenAddress].name[0];
              var symbol = response[tokenAddress].symbol[0];
              var totalSupplyUnformatted = response[tokenAddress].totalSupply[0];
              var totalSupply = formatUnits(totalSupplyUnformatted, decimals);
              var totalSupplyPretty = numberWithCommas(totalSupply);
              result[tokenAddress] = {
                address: tokenAddress,
                hasBalance: !amountUnformatted.isZero(),
                amount: amount,
                amountPretty: amountPretty,
                amountUnformatted: amountUnformatted,
                decimals: decimals,
                name: name,
                symbol: symbol,
                totalSupply: totalSupply,
                totalSupplyPretty: totalSupplyPretty,
                totalSupplyUnformatted: totalSupplyUnformatted
              };
            });
            return _context2.abrupt("return", result);

          case 9:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getTokenBalances(_x, _x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();

/**
 * Fetch token prices across multiple chains with a single hook
 * @param contractAddresses
 * @returns
 */

var useCoingeckoTokenPricesAcrossChains = function useCoingeckoTokenPricesAcrossChains(contractAddresses) {
  var chainIds = contractAddresses ? Object.keys(contractAddresses).map(Number) : [];
  var queriesResult = useQueries(chainIds.map(function (chainId) {
    var validNetworks = Object.keys(COINGECKO_ASSET_PLATFORMS);
    var isValidNetwork = validNetworks.includes(chainId.toString());
    var isValidAddresses = contractAddresses[chainId].reduce(function (isValid, contractAddress) {
      return isValid && isAddress(contractAddress);
    }, true);
    var assetPlatform = COINGECKO_ASSET_PLATFORMS[chainId];
    var enabled = Boolean(contractAddresses[chainId]) && Boolean(isValidAddresses) && isValidNetwork && Boolean(chainId) && Boolean(assetPlatform);
    return _extends({}, NO_REFETCH, {
      staleTime: Infinity,
      queryKey: [QUERY_KEYS.getCoingeckoTokenPrices, contractAddresses[chainId], chainId],
      queryFn: function () {
        var _queryFn = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
          return _regeneratorRuntime().wrap(function _callee$(_context) {
            while (1) {
              switch (_context.prev = _context.next) {
                case 0:
                  return _context.abrupt("return", getCoingeckoTokenPrices(chainId, contractAddresses[chainId]));

                case 1:
                case "end":
                  return _context.stop();
              }
            }
          }, _callee);
        }));

        function queryFn() {
          return _queryFn.apply(this, arguments);
        }

        return queryFn;
      }(),
      enabled: enabled
    });
  }));
  return useMemo(function () {
    var isFetched = queriesResult == null ? void 0 : queriesResult.every(function (queryResult) {
      return queryResult.isFetched;
    });

    var refetch = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
        return _regeneratorRuntime().wrap(function _callee2$(_context2) {
          while (1) {
            switch (_context2.prev = _context2.next) {
              case 0:
                return _context2.abrupt("return", queriesResult == null ? void 0 : queriesResult.forEach(function (queryResult) {
                  return queryResult.refetch();
                }));

              case 1:
              case "end":
                return _context2.stop();
            }
          }
        }, _callee2);
      }));

      return function refetch() {
        return _ref.apply(this, arguments);
      };
    }();

    return {
      isFetched: isFetched,
      refetch: refetch,
      data: (queriesResult == null ? void 0 : queriesResult.reduce(function (data, queryResult) {
        if (queryResult.isFetched && !!queryResult.data && typeof queryResult.data === 'object') {
          return _extends({}, data, queryResult.data);
        } else {
          return data;
        }
      }, {})) || {}
    };
  }, [queriesResult]);
};
var useCoingeckoTokenPrices = function useCoingeckoTokenPrices(chainId, contractAddresses) {
  var validNetworks = Object.keys(COINGECKO_ASSET_PLATFORMS);
  var isValidNetwork = validNetworks.includes(chainId.toString());
  var isValidAddresses = contractAddresses == null ? void 0 : contractAddresses.reduce(function (isValid, contractAddress) {
    return isValid && isAddress(contractAddress);
  }, true);
  var assetPlatform = COINGECKO_ASSET_PLATFORMS[chainId];
  var enabled = Boolean(contractAddresses) && Boolean(isValidAddresses) && isValidNetwork && Boolean(chainId) && Boolean(assetPlatform);
  return useQuery([QUERY_KEYS.getCoingeckoTokenPrices, contractAddresses, chainId], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3() {
    return _regeneratorRuntime().wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return getCoingeckoTokenPrices(chainId, contractAddresses);

          case 2:
            return _context3.abrupt("return", _context3.sent);

          case 3:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  })), _extends({
    staleTime: Infinity,
    enabled: enabled
  }, NO_REFETCH));
};
/**
 * @param chainId
 * @param contractAddresses
 * @returns
 */

var getCoingeckoTokenPrices = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4(chainId, contractAddresses) {
    var assetPlatform, url, response, tokenPrices, tokenPricesFormatted;
    return _regeneratorRuntime().wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            assetPlatform = COINGECKO_ASSET_PLATFORMS[chainId]; // Hardcode some prices for Goerli & Mumbai testing

            if (!(assetPlatform === COINGECKO_ASSET_PLATFORMS[NETWORK.goerli] || assetPlatform === COINGECKO_ASSET_PLATFORMS[NETWORK.mumbai])) {
              _context4.next = 3;
              break;
            }

            return _context4.abrupt("return", contractAddresses.reduce(function (tokenPrices, contractAddress) {
              tokenPrices[getAddress(contractAddress)] = {
                usd: 1
              };
              tokenPrices[contractAddress.toLowerCase()] = {
                usd: 1
              };
              return tokenPrices;
            }, {}));

          case 3:
            _context4.prev = 3;
            url = new URL(COINGECKO_API_URL + "/simple/token_price/" + assetPlatform);
            url.searchParams.set('contract_addresses', contractAddresses.join(','));
            url.searchParams.set('vs_currencies', 'usd');
            _context4.next = 9;
            return fetch(url.toString());

          case 9:
            response = _context4.sent;
            _context4.next = 12;
            return response.json();

          case 12:
            tokenPrices = _context4.sent;
            tokenPricesFormatted = {};
            Object.keys(tokenPrices).forEach(function (tokenAddress) {
              var tokenPrice = tokenPrices[getAddress(tokenAddress)] || tokenPrices[tokenAddress.toLowerCase()];
              tokenPricesFormatted[getAddress(tokenAddress)] = tokenPrice;
              tokenPricesFormatted[tokenAddress.toLowerCase()] = tokenPrice;
            });
            return _context4.abrupt("return", tokenPricesFormatted);

          case 18:
            _context4.prev = 18;
            _context4.t0 = _context4["catch"](3);
            console.error(_context4.t0.message);
            return _context4.abrupt("return", {});

          case 22:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4, null, [[3, 18]]);
  }));

  return function getCoingeckoTokenPrices(_x, _x2) {
    return _ref3.apply(this, arguments);
  };
}();

/**
 * NOTE: Relies on constants for getting tokens in the LP pair so we can start fetching the token prices faster & use the coingecko token price cache
 * @param prizePool
 * @returns
 */

var useLPTokenUsdValue = function useLPTokenUsdValue(prizePool) {
  var _tokenPrices$token1Ad, _tokenPrices$token2Ad;

  var lpPrizePoolMetadata = getLPPrizePoolMetadata(prizePool);
  var lpTokenAddress = lpPrizePoolMetadata.tokens.underlyingToken.address;
  var token1Address = lpPrizePoolMetadata.tokens.underlyingToken.token1.address;
  var token2Address = lpPrizePoolMetadata.tokens.underlyingToken.token2.address;

  var _useCoingeckoTokenPri = useCoingeckoTokenPrices(prizePool.chainId, [token1Address, token2Address]),
      tokenPrices = _useCoingeckoTokenPri.data,
      isTokenPricesFetched = _useCoingeckoTokenPri.isFetched;

  var _useTokenBalances = useTokenBalances(prizePool.chainId, lpPrizePoolMetadata.tokens.underlyingToken.address, [token1Address, token2Address, lpTokenAddress]),
      lPTokenBalances = _useTokenBalances.data,
      tokenBalancesIsFetched = _useTokenBalances.isFetched;

  var isFetched = isTokenPricesFetched && tokenBalancesIsFetched;
  var token1BalanceData = tokenBalancesIsFetched && lPTokenBalances[token1Address];
  var token1ValueUsd = tokenPrices && (tokenPrices == null ? void 0 : (_tokenPrices$token1Ad = tokenPrices[token1Address]) == null ? void 0 : _tokenPrices$token1Ad.usd);
  var token2BalanceData = tokenBalancesIsFetched && lPTokenBalances[token2Address];
  var token2ValueUsd = tokenPrices && (tokenPrices == null ? void 0 : (_tokenPrices$token2Ad = tokenPrices[token2Address]) == null ? void 0 : _tokenPrices$token2Ad.usd);
  var lpTokenValueUsd = isFetched && !!token1BalanceData && !!token2BalanceData && !!token1ValueUsd && !!token2ValueUsd ? calculateLPTokenPrice(formatUnits(token1BalanceData.amountUnformatted, token1BalanceData.decimals), formatUnits(token2BalanceData.amountUnformatted, token2BalanceData.decimals), token1ValueUsd, token2ValueUsd, lPTokenBalances[lpTokenAddress].totalSupply) : null;
  return {
    data: lpTokenValueUsd == null ? void 0 : lpTokenValueUsd.toNumber(),
    isFetched: isFetched
  };
};
var getLPPrizePoolMetadata = function getLPPrizePoolMetadata(prizePool) {
  var _V3$LP_PRIZE_POOL_MET;

  return (_V3$LP_PRIZE_POOL_MET = V3.LP_PRIZE_POOL_METADATA[prizePool.chainId]) == null ? void 0 : _V3$LP_PRIZE_POOL_MET.find(function (lpPrizePool) {
    return lpPrizePool.prizePool === prizePool.addresses.prizePool;
  });
};

var TokenFaucetAbi = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'newTokens',
    type: 'uint256'
  }],
  name: 'Claimed',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'Deposited',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'dripRatePerSecond',
    type: 'uint256'
  }],
  name: 'DripRateChanged',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'newTokens',
    type: 'uint256'
  }],
  name: 'Dripped',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC20Upgradeable',
    name: 'asset',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'contract IERC20Upgradeable',
    name: 'measure',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'dripRatePerSecond',
    type: 'uint256'
  }],
  name: 'Initialized',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'previousOwner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'OwnershipTransferred',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'Withdrawn',
  type: 'event'
}, {
  inputs: [],
  name: 'asset',
  outputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  name: 'beforeTokenMint',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'token',
    type: 'address'
  }],
  name: 'beforeTokenTransfer',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'user',
    type: 'address'
  }],
  name: 'claim',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'deposit',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'drip',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'dripRatePerSecond',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'exchangeRateMantissa',
  outputs: [{
    internalType: 'uint112',
    name: '',
    type: 'uint112'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '_asset',
    type: 'address'
  }, {
    internalType: 'contract IERC20Upgradeable',
    name: '_measure',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: '_dripRatePerSecond',
    type: 'uint256'
  }],
  name: 'initialize',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'lastDripTimestamp',
  outputs: [{
    internalType: 'uint32',
    name: '',
    type: 'uint32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'measure',
  outputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'owner',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'renounceOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: '_dripRatePerSecond',
    type: 'uint256'
  }],
  name: 'setDripRatePerSecond',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'bytes4',
    name: 'interfaceId',
    type: 'bytes4'
  }],
  name: 'supportsInterface',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'totalUnclaimed',
  outputs: [{
    internalType: 'uint112',
    name: '',
    type: 'uint112'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'transferOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  name: 'userStates',
  outputs: [{
    internalType: 'uint128',
    name: 'lastExchangeRateMantissa',
    type: 'uint128'
  }, {
    internalType: 'uint128',
    name: 'balance',
    type: 'uint128'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'withdrawTo',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}];

var useTokenFaucetData = function useTokenFaucetData(chainId, tokenFaucetAddress, prizePool, underlyingToken) {
  var enabled = !!(underlyingToken != null && underlyingToken.usdPerToken);
  return useQuery(['useTokenFaucetData', chainId, tokenFaucetAddress], function () {
    return getTokenFaucetData(chainId, tokenFaucetAddress, prizePool, underlyingToken);
  }, {
    enabled: enabled
  });
};

var getTokenFaucetData = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(chainId, tokenFaucetAddress, prizePool, underlyingToken) {
    var provider, batchRequests, tokenFaucetContract, tokenFaucetResults, dripTokenAddress, measureTokenAddress, dripTokenContract, measureTokenContract, tokenResults, measureToken, dripToken, tokenPrices, dripTokenValueUsd, dripRatePerSecondUnformatted, dripRatePerDayUnformatted, dripRatePerSecond, totalUnclaimedUnformatted, faucetsDripTokenBalanceUnformatted, remainingDripTokenBalanceUnformatted, remainingDaysUnformatted, remainingDays, measureTokenTotalSupplyUnformatted, totalDripPerDay, totalDripDailyValue, totalMeasureTokenValueUsdUnformatted, totalMeasureTokenValueUsd, vapr;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            provider = getReadProvider(chainId);
            batchRequests = []; // Fetch data from token faucet

            tokenFaucetContract = contract(tokenFaucetAddress, TokenFaucetAbi, tokenFaucetAddress);
            batchRequests.push(tokenFaucetContract.dripRatePerSecond().measure().asset().totalUnclaimed());
            _context.next = 6;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 6:
            tokenFaucetResults = _context.sent;
            batchRequests = [];
            dripTokenAddress = tokenFaucetResults[tokenFaucetAddress].asset[0];
            measureTokenAddress = tokenFaucetResults[tokenFaucetAddress].measure[0]; // Fetch data from tokens

            dripTokenContract = contract(dripTokenAddress, ERC20Abi, dripTokenAddress);
            measureTokenContract = contract(measureTokenAddress, ERC20Abi, measureTokenAddress);
            batchRequests.push(dripTokenContract.name().symbol().decimals().balanceOf(tokenFaucetAddress), measureTokenContract.totalSupply());
            _context.next = 15;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 15:
            tokenResults = _context.sent;
            batchRequests = [];
            measureToken = getMeasureToken(measureTokenAddress, prizePool);
            dripToken = {
              address: dripTokenAddress,
              decimals: tokenResults[dripTokenAddress].decimals[0],
              name: tokenResults[dripTokenAddress].name[0],
              symbol: tokenResults[dripTokenAddress].symbol[0]
            };
            _context.prev = 19;
            _context.next = 22;
            return getCoingeckoTokenPrices(chainId, [dripTokenAddress]);

          case 22:
            tokenPrices = _context.sent;
            dripTokenValueUsd = tokenPrices[dripTokenAddress].usd;
            dripRatePerSecondUnformatted = tokenFaucetResults[tokenFaucetAddress].dripRatePerSecond[0];
            dripRatePerDayUnformatted = dripRatePerSecondUnformatted.mul(SECONDS_PER_DAY);
            dripRatePerSecond = formatUnits(dripRatePerSecondUnformatted, dripToken.decimals);
            totalUnclaimedUnformatted = tokenFaucetResults[tokenFaucetAddress].totalUnclaimed[0];
            faucetsDripTokenBalanceUnformatted = tokenResults[dripTokenAddress].balanceOf[0];
            remainingDripTokenBalanceUnformatted = faucetsDripTokenBalanceUnformatted.sub(totalUnclaimedUnformatted);
            remainingDaysUnformatted = remainingDripTokenBalanceUnformatted.mul(100).div(dripRatePerDayUnformatted);
            remainingDays = Number(remainingDaysUnformatted.toString()) / 100;
            remainingDaysUnformatted = remainingDaysUnformatted.div(100);
            measureTokenTotalSupplyUnformatted = tokenResults[measureTokenAddress].totalSupply[0];
            totalDripPerDay = Number(dripRatePerSecond) * SECONDS_PER_DAY;
            totalDripDailyValue = totalDripPerDay * dripTokenValueUsd;
            totalMeasureTokenValueUsdUnformatted = amountMultByUsd(measureTokenTotalSupplyUnformatted, underlyingToken.usdPerToken);
            totalMeasureTokenValueUsd = Number(formatUnits(totalMeasureTokenValueUsdUnformatted, measureToken.decimals));
            vapr = 0;

            if (remainingDays > 0) {
              vapr = totalDripDailyValue / totalMeasureTokenValueUsd * 365 * 100;
            }

            return _context.abrupt("return", {
              vapr: vapr,
              dripToken: dripToken,
              dripTokenValueUsd: dripTokenValueUsd,
              measureToken: measureToken,
              measureTokenValueUsd: underlyingToken.usdPerToken
            });

          case 43:
            _context.prev = 43;
            _context.t0 = _context["catch"](19);
            return _context.abrupt("return", {
              vapr: 0,
              dripToken: dripToken,
              dripTokenValueUsd: 0,
              measureToken: measureToken,
              measureTokenValueUsd: underlyingToken.usdPerToken
            });

          case 46:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[19, 43]]);
  }));

  return function getTokenFaucetData(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Returns the measure token from the prize pool
 * @param measureTokenAddress
 * @param prizePool
 * @returns
 */


var getMeasureToken = function getMeasureToken(measureTokenAddress, prizePool) {
  if (measureTokenAddress === prizePool.addresses.sponsorship) {
    return prizePool.tokens.sponsorship;
  }

  return prizePool.tokens.ticket;
};

var PodAbi = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Approval',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'BatchFloat',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'balance',
    type: 'uint256'
  }],
  name: 'Claimed',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'shares',
    type: 'uint256'
  }],
  name: 'Deposited',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC20Upgradeable',
    name: 'target',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'ERC20Withdrawn',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC721Upgradeable',
    name: 'target',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'tokenId',
    type: 'uint256'
  }],
  name: 'ERC721Withdrawn',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'previousmanager',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'newmanager',
    type: 'address'
  }],
  name: 'ManagementTransferred',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'previousOwner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'OwnershipTransferred',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'PodClaimed',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract TokenDrop',
    name: 'drop',
    type: 'address'
  }],
  name: 'TokenDropSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract TokenFaucet',
    name: 'drop',
    type: 'address'
  }],
  name: 'TokenFaucetSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'value',
    type: 'uint256'
  }],
  name: 'Transfer',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'shares',
    type: 'uint256'
  }],
  name: 'Withdrawal',
  type: 'event'
}, {
  inputs: [{
    internalType: 'address',
    name: 'owner',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }],
  name: 'allowance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'approve',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'balance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'account',
    type: 'address'
  }],
  name: 'balanceOf',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'user',
    type: 'address'
  }],
  name: 'balanceOfUnderlying',
  outputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'batch',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'decimals',
  outputs: [{
    internalType: 'uint8',
    name: '',
    type: 'uint8'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'subtractedValue',
    type: 'uint256'
  }],
  name: 'decreaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'tokenAmount',
    type: 'uint256'
  }],
  name: 'depositTo',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'drop',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '_token',
    type: 'address'
  }, {
    internalType: 'address',
    name: '_target',
    type: 'address'
  }],
  name: 'emergencyTokenApproveZero',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'faucet',
  outputs: [{
    internalType: 'contract TokenFaucet',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'getEarlyExitFee',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'getPricePerShare',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'spender',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'addedValue',
    type: 'uint256'
  }],
  name: 'increaseAllowance',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: '_prizePoolTarget',
    type: 'address'
  }, {
    internalType: 'address',
    name: '_ticket',
    type: 'address'
  }, {
    internalType: 'uint8',
    name: '_decimals',
    type: 'uint8'
  }],
  name: 'initialize',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'manager',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'name',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'owner',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePool',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'renounceOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'newManager',
    type: 'address'
  }],
  name: 'setManager',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract TokenDrop',
    name: '_tokenDrop',
    type: 'address'
  }],
  name: 'setTokenDrop',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract TokenFaucet',
    name: '_faucet',
    type: 'address'
  }],
  name: 'setTokenFaucet',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'symbol',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'ticket',
  outputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token',
  outputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'tokenDrop',
  outputs: [{
    internalType: 'contract TokenDrop',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'totalSupply',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'recipient',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'transfer',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'sender',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'recipient',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'transferFrom',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'transferOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'shareAmount',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'maxFee',
    type: 'uint256'
  }],
  name: 'withdraw',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '_target',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'withdrawERC20',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC721Upgradeable',
    name: '_target',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'tokenId',
    type: 'uint256'
  }],
  name: 'withdrawERC721',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}];

var PrizePoolAbi = [{
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'AwardCaptured',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'winner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'Awarded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'winner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'AwardedExternalERC20',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'winner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256[]',
    name: 'tokenIds',
    type: 'uint256[]'
  }],
  name: 'AwardedExternalERC721',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract ControlledTokenInterface',
    name: 'token',
    type: 'address'
  }],
  name: 'ControlledTokenAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'CreditBurned',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'CreditMinted',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint128',
    name: 'creditLimitMantissa',
    type: 'uint128'
  }, {
    indexed: false,
    internalType: 'uint128',
    name: 'creditRateMantissa',
    type: 'uint128'
  }],
  name: 'CreditPlanSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'address',
    name: 'referrer',
    type: 'address'
  }],
  name: 'Deposited',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'address',
    name: 'reserveRegistry',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'maxExitFeeMantissa',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'maxTimelockDuration',
    type: 'uint256'
  }],
  name: 'Initialized',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'redeemed',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'exitFee',
    type: 'uint256'
  }],
  name: 'InstantWithdrawal',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'liquidityCap',
    type: 'uint256'
  }],
  name: 'LiquidityCapSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'previousOwner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'OwnershipTransferred',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'prizeStrategy',
    type: 'address'
  }],
  name: 'PrizeStrategySet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'ReserveFeeCaptured',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'ReserveWithdrawal',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'TimelockDeposited',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'unlockTimestamp',
    type: 'uint256'
  }],
  name: 'TimelockedWithdrawal',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'redeemed',
    type: 'uint256'
  }],
  name: 'TimelockedWithdrawalSwept',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'token',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'TransferredExternalERC20',
  type: 'event'
}, {
  inputs: [],
  name: 'accountedBalance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'award',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'awardBalance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'externalToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'awardExternalERC20',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'externalToken',
    type: 'address'
  }, {
    internalType: 'uint256[]',
    name: 'tokenIds',
    type: 'uint256[]'
  }],
  name: 'awardExternalERC721',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'balance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'user',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'balanceOfCredit',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'beforeTokenTransfer',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'calculateEarlyExitFee',
  outputs: [{
    internalType: 'uint256',
    name: 'exitFee',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'burnedCredit',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'calculateReserveFee',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'calculateTimelockDuration',
  outputs: [{
    internalType: 'uint256',
    name: 'durationSeconds',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'burnedCredit',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: '_externalToken',
    type: 'address'
  }],
  name: 'canAwardExternal',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'captureAwardBalance',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract ICompLike',
    name: 'compLike',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }],
  name: 'compLikeDelegate',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'creditPlanOf',
  outputs: [{
    internalType: 'uint128',
    name: 'creditLimitMantissa',
    type: 'uint128'
  }, {
    internalType: 'uint128',
    name: 'creditRateMantissa',
    type: 'uint128'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'referrer',
    type: 'address'
  }],
  name: 'depositTo',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: '_controlledToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: '_principal',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: '_interest',
    type: 'uint256'
  }],
  name: 'estimateCreditAccrualTime',
  outputs: [{
    internalType: 'uint256',
    name: 'durationSeconds',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract RegistryInterface',
    name: '_reserveRegistry',
    type: 'address'
  }, {
    internalType: 'contract ControlledTokenInterface[]',
    name: '_controlledTokens',
    type: 'address[]'
  }, {
    internalType: 'uint256',
    name: '_maxExitFeeMantissa',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: '_maxTimelockDuration',
    type: 'uint256'
  }],
  name: 'initialize',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'liquidityCap',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'maxExitFeeMantissa',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'maxTimelockDuration',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'owner',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizeStrategy',
  outputs: [{
    internalType: 'contract TokenListenerInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'renounceOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'reserveRegistry',
  outputs: [{
    internalType: 'contract RegistryInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'reserveTotalSupply',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: '_controlledToken',
    type: 'address'
  }, {
    internalType: 'uint128',
    name: '_creditRateMantissa',
    type: 'uint128'
  }, {
    internalType: 'uint128',
    name: '_creditLimitMantissa',
    type: 'uint128'
  }],
  name: 'setCreditPlanOf',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: '_liquidityCap',
    type: 'uint256'
  }],
  name: 'setLiquidityCap',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract TokenListenerInterface',
    name: '_prizeStrategy',
    type: 'address'
  }],
  name: 'setPrizeStrategy',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address[]',
    name: 'users',
    type: 'address[]'
  }],
  name: 'sweepTimelockBalances',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'user',
    type: 'address'
  }],
  name: 'timelockBalanceAvailableAt',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'user',
    type: 'address'
  }],
  name: 'timelockBalanceOf',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'timelockDepositTo',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'timelockTotalSupply',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'tokens',
  outputs: [{
    internalType: 'address[]',
    name: '',
    type: 'address[]'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'externalToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'transferExternalERC20',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'transferOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'maximumExitFee',
    type: 'uint256'
  }],
  name: 'withdrawInstantlyFrom',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }],
  name: 'withdrawReserve',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'withdrawWithTimelockFrom',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'nonpayable',
  type: 'function'
}];

var PrizeStrategyAbi_3_4_4 = [{
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract BeforeAwardListenerInterface',
    name: 'beforeAwardListener',
    type: 'address'
  }],
  name: 'BeforeAwardListenerSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC20Upgradeable',
    name: 'externalErc20',
    type: 'address'
  }],
  name: 'ExternalErc20AwardAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC20Upgradeable',
    name: 'externalErc20Award',
    type: 'address'
  }],
  name: 'ExternalErc20AwardRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC721Upgradeable',
    name: 'externalErc721',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256[]',
    name: 'tokenIds',
    type: 'uint256[]'
  }],
  name: 'ExternalErc721AwardAdded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract IERC721Upgradeable',
    name: 'externalErc721Award',
    type: 'address'
  }],
  name: 'ExternalErc721AwardRemoved',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'prizePeriodStart',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'prizePeriodSeconds',
    type: 'uint256'
  }, {
    indexed: true,
    internalType: 'contract PrizePool',
    name: 'prizePool',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'contract TicketInterface',
    name: 'ticket',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'contract IERC20Upgradeable',
    name: 'sponsorship',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'contract RNGInterface',
    name: 'rng',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'contract IERC20Upgradeable[]',
    name: 'externalErc20Awards',
    type: 'address[]'
  }],
  name: 'Initialized',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'previousOwner',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'OwnershipTransferred',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract PeriodicPrizeStrategyListenerInterface',
    name: 'periodicPrizeStrategyListener',
    type: 'address'
  }],
  name: 'PeriodicPrizeStrategyListenerSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'prizePeriodSeconds',
    type: 'uint256'
  }],
  name: 'PrizePeriodSecondsUpdated',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'prizePool',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'uint32',
    name: 'rngRequestId',
    type: 'uint32'
  }, {
    indexed: false,
    internalType: 'uint32',
    name: 'rngLockBlock',
    type: 'uint32'
  }],
  name: 'PrizePoolAwardCancelled',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'address',
    name: 'prizePool',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'uint32',
    name: 'rngRequestId',
    type: 'uint32'
  }, {
    indexed: false,
    internalType: 'uint32',
    name: 'rngLockBlock',
    type: 'uint32'
  }],
  name: 'PrizePoolAwardStarted',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'randomNumber',
    type: 'uint256'
  }],
  name: 'PrizePoolAwarded',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'address',
    name: 'operator',
    type: 'address'
  }, {
    indexed: true,
    internalType: 'uint256',
    name: 'prizePeriodStartedAt',
    type: 'uint256'
  }],
  name: 'PrizePoolOpened',
  type: 'event'
}, {
  anonymous: false,
  inputs: [],
  name: 'RngRequestFailed',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint32',
    name: 'rngRequestTimeout',
    type: 'uint32'
  }],
  name: 'RngRequestTimeoutSet',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract RNGInterface',
    name: 'rngService',
    type: 'address'
  }],
  name: 'RngServiceUpdated',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: true,
    internalType: 'contract TokenListenerInterface',
    name: 'tokenListener',
    type: 'address'
  }],
  name: 'TokenListenerUpdated',
  type: 'event'
}, {
  inputs: [],
  name: 'VERSION',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '_externalErc20',
    type: 'address'
  }],
  name: 'addExternalErc20Award',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable[]',
    name: '_externalErc20s',
    type: 'address[]'
  }],
  name: 'addExternalErc20Awards',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC721Upgradeable',
    name: '_externalErc721',
    type: 'address'
  }, {
    internalType: 'uint256[]',
    name: '_tokenIds',
    type: 'uint256[]'
  }],
  name: 'addExternalErc721Award',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'beforeAwardListener',
  outputs: [{
    internalType: 'contract BeforeAwardListenerInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'referrer',
    type: 'address'
  }],
  name: 'beforeTokenMint',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'from',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'to',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'controlledToken',
    type: 'address'
  }],
  name: 'beforeTokenTransfer',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'currentTime',
    type: 'uint256'
  }],
  name: 'calculateNextPrizePeriodStartTime',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'canCompleteAward',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'canStartAward',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'cancelAward',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'completeAward',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'currentPrize',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'secondsPerBlockMantissa',
    type: 'uint256'
  }],
  name: 'estimateRemainingBlocksToPrize',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getExternalErc20Awards',
  outputs: [{
    internalType: 'address[]',
    name: '',
    type: 'address[]'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC721Upgradeable',
    name: '_externalErc721',
    type: 'address'
  }],
  name: 'getExternalErc721AwardTokenIds',
  outputs: [{
    internalType: 'uint256[]',
    name: '',
    type: 'uint256[]'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getExternalErc721Awards',
  outputs: [{
    internalType: 'address[]',
    name: '',
    type: 'address[]'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getLastRngLockBlock',
  outputs: [{
    internalType: 'uint32',
    name: '',
    type: 'uint32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'getLastRngRequestId',
  outputs: [{
    internalType: 'uint32',
    name: '',
    type: 'uint32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: '_prizePeriodStart',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: '_prizePeriodSeconds',
    type: 'uint256'
  }, {
    internalType: 'contract PrizePool',
    name: '_prizePool',
    type: 'address'
  }, {
    internalType: 'contract TicketInterface',
    name: '_ticket',
    type: 'address'
  }, {
    internalType: 'contract IERC20Upgradeable',
    name: '_sponsorship',
    type: 'address'
  }, {
    internalType: 'contract RNGInterface',
    name: '_rng',
    type: 'address'
  }, {
    internalType: 'contract IERC20Upgradeable[]',
    name: 'externalErc20Awards',
    type: 'address[]'
  }],
  name: 'initialize',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'isPrizePeriodOver',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'isRngCompleted',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'isRngRequested',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'isRngTimedOut',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'owner',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'periodicPrizeStrategyListener',
  outputs: [{
    internalType: 'contract PeriodicPrizeStrategyListenerInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePeriodEndAt',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePeriodRemainingSeconds',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePeriodSeconds',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePeriodStartedAt',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'prizePool',
  outputs: [{
    internalType: 'contract PrizePool',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '_externalErc20',
    type: 'address'
  }, {
    internalType: 'contract IERC20Upgradeable',
    name: '_prevExternalErc20',
    type: 'address'
  }],
  name: 'removeExternalErc20Award',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract IERC721Upgradeable',
    name: '_externalErc721',
    type: 'address'
  }, {
    internalType: 'contract IERC721Upgradeable',
    name: '_prevExternalErc721',
    type: 'address'
  }],
  name: 'removeExternalErc721Award',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'renounceOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'rng',
  outputs: [{
    internalType: 'contract RNGInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'rngRequestTimeout',
  outputs: [{
    internalType: 'uint32',
    name: '',
    type: 'uint32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract BeforeAwardListenerInterface',
    name: '_beforeAwardListener',
    type: 'address'
  }],
  name: 'setBeforeAwardListener',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract PeriodicPrizeStrategyListenerInterface',
    name: '_periodicPrizeStrategyListener',
    type: 'address'
  }],
  name: 'setPeriodicPrizeStrategyListener',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: '_prizePeriodSeconds',
    type: 'uint256'
  }],
  name: 'setPrizePeriodSeconds',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint32',
    name: '_rngRequestTimeout',
    type: 'uint32'
  }],
  name: 'setRngRequestTimeout',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract RNGInterface',
    name: 'rngService',
    type: 'address'
  }],
  name: 'setRngService',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'contract TokenListenerInterface',
    name: '_tokenListener',
    type: 'address'
  }],
  name: 'setTokenListener',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [],
  name: 'sponsorship',
  outputs: [{
    internalType: 'contract IERC20Upgradeable',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'startAward',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'bytes4',
    name: 'interfaceId',
    type: 'bytes4'
  }],
  name: 'supportsInterface',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'ticket',
  outputs: [{
    internalType: 'contract TicketInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'tokenListener',
  outputs: [{
    internalType: 'contract TokenListenerInterface',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [{
    internalType: 'address',
    name: 'newOwner',
    type: 'address'
  }],
  name: 'transferOwnership',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}];

var useV3ChainIds = function useV3ChainIds() {
  var appEnv = getAppEnvString();
  return V3.CHAIN_IDS[appEnv];
};

/**
 * Fetches v3 Prize Pool contract addresses and token data
 * @returns
 */

var useV3PrizePools = function useV3PrizePools() {
  var chainIds = useV3ChainIds();
  var providers = getReadProviders(chainIds);
  return useQuery(['useV3PrizePools', chainIds], function () {
    return getV3PrizePools(chainIds, providers);
  });
};
/**
 * Fetch all of the data on a per chain id basis and merge data
 * @param chainIds
 * @param providers
 * @returns
 */

var getV3PrizePools = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(chainIds, providers) {
    var prizePoolAddressByChainId, prizePoolsByChainId;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            prizePoolAddressByChainId = getPrizePoolAddresses(chainIds);
            prizePoolsByChainId = {};
            _context2.next = 4;
            return Promise.allSettled(chainIds.map( /*#__PURE__*/function () {
              var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(chainId) {
                var prizePoolAddresses, prizePools;
                return _regeneratorRuntime().wrap(function _callee$(_context) {
                  while (1) {
                    switch (_context.prev = _context.next) {
                      case 0:
                        prizePoolAddresses = prizePoolAddressByChainId[chainId];
                        _context.prev = 1;
                        _context.next = 4;
                        return getPrizePools(chainId, providers[chainId], prizePoolAddresses);

                      case 4:
                        prizePools = _context.sent;
                        prizePoolsByChainId[chainId] = prizePools;
                        _context.next = 11;
                        break;

                      case 8:
                        _context.prev = 8;
                        _context.t0 = _context["catch"](1);
                        console.error(_context.t0.message);

                      case 11:
                      case "end":
                        return _context.stop();
                    }
                  }
                }, _callee, null, [[1, 8]]);
              }));

              return function (_x3) {
                return _ref2.apply(this, arguments);
              };
            }()));

          case 4:
            return _context2.abrupt("return", prizePoolsByChainId);

          case 5:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getV3PrizePools(_x, _x2) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Filter the constant for just the prize pool addresses
 * @param chainIds
 * @returns
 */


var getPrizePoolAddresses = function getPrizePoolAddresses(chainIds) {
  var prizePoolAddresses = {};
  chainIds == null ? void 0 : chainIds.forEach(function (chainId) {
    var _V3$PRIZE_POOL_ADDRES, _V3$PRIZE_POOL_ADDRES2;

    prizePoolAddresses[chainId] = ((_V3$PRIZE_POOL_ADDRES = V3.PRIZE_POOL_ADDRESSES) == null ? void 0 : (_V3$PRIZE_POOL_ADDRES2 = _V3$PRIZE_POOL_ADDRES[chainId]) == null ? void 0 : _V3$PRIZE_POOL_ADDRES2.map(function (prizePool) {
      return prizePool.prizePool;
    })) || [];
  });
  return prizePoolAddresses;
};
/**
 
 * @param chainIds
 * @returns
 */


var getPrizePoolTokenFaucetAddresses = function getPrizePoolTokenFaucetAddresses(chainId, prizePoolAddress) {
  var _V3$PRIZE_POOL_ADDRES3, _V3$PRIZE_POOL_ADDRES4, _V3$PRIZE_POOL_ADDRES5;

  return ((_V3$PRIZE_POOL_ADDRES3 = V3.PRIZE_POOL_ADDRESSES) == null ? void 0 : (_V3$PRIZE_POOL_ADDRES4 = _V3$PRIZE_POOL_ADDRES3[chainId]) == null ? void 0 : (_V3$PRIZE_POOL_ADDRES5 = _V3$PRIZE_POOL_ADDRES4.find(function (_ref3) {
    var prizePool = _ref3.prizePool;
    return prizePool === prizePoolAddress;
  })) == null ? void 0 : _V3$PRIZE_POOL_ADDRES5.tokenFaucets) || [];
};
/**
 * Fetch the prize pool contract addresses and then the token data
 * @param provider
 * @param prizePoolAddresses
 * @returns
 */


var getPrizePools = /*#__PURE__*/function () {
  var _ref4 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(chainId, provider, prizePoolAddresses) {
    var batchRequests, prizePoolContractAddressesResults, prizeStrategyContractAddressesResults, tokenResults, v3PrizePools;
    return _regeneratorRuntime().wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            batchRequests = [];

            if (prizePoolAddresses.length) {
              _context3.next = 3;
              break;
            }

            return _context3.abrupt("return", []);

          case 3:
            // Fetch addresses from Prize Pool contract
            prizePoolAddresses.forEach(function (prizePoolAddress) {
              var prizePoolContract = contract(prizePoolAddress, PrizePoolAbi, prizePoolAddress);
              batchRequests.push(prizePoolContract.prizeStrategy().token());
            });
            _context3.next = 6;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 6:
            prizePoolContractAddressesResults = _context3.sent;
            batchRequests = []; // Fetch addresses from Prize Strategy contract

            prizePoolAddresses.forEach(function (prizePoolAddress) {
              var prizeStrategyAddress = prizePoolContractAddressesResults[prizePoolAddress].prizeStrategy[0];
              var prizeStrategyContract = contract(prizeStrategyAddress, PrizeStrategyAbi_3_4_4, prizeStrategyAddress);
              batchRequests.push(prizeStrategyContract.sponsorship().ticket());
            });
            _context3.next = 11;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 11:
            prizeStrategyContractAddressesResults = _context3.sent;
            batchRequests = []; // Fetch Token data for prize pools

            prizePoolAddresses.forEach(function (prizePoolAddress) {
              var prizeStrategyAddress = prizePoolContractAddressesResults[prizePoolAddress].prizeStrategy[0];
              var tokenAddress = prizePoolContractAddressesResults[prizePoolAddress].token[0];
              var prizeStrategyAddresses = prizeStrategyContractAddressesResults[prizeStrategyAddress];
              var ticketAddress = prizeStrategyAddresses.ticket[0];
              var sponsorshipTicketAddress = prizeStrategyAddresses.sponsorship[0];
              var ticketContract = contract(ticketAddress, ERC20Abi, ticketAddress);
              var tokenContract = contract(tokenAddress, ERC20Abi, tokenAddress);
              var sponsorshipTicketContract = contract(sponsorshipTicketAddress, ERC20Abi, sponsorshipTicketAddress);
              batchRequests.push(ticketContract.name().symbol().decimals(), tokenContract.name().symbol().decimals(), sponsorshipTicketContract.name().symbol().decimals()); // Fetch Pod data

              var podAddress = getPodAddress(chainId, prizePoolAddress);

              if (!!podAddress) {
                var podStablecoinContract = contract(podAddress, PodAbi, podAddress);
                batchRequests.push(podStablecoinContract.name().symbol().decimals().getPricePerShare());
              }
            });
            _context3.next = 16;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 16:
            tokenResults = _context3.sent;
            batchRequests = [];
            v3PrizePools = []; // Final, build

            prizePoolAddresses.forEach(function (prizePoolAddress) {
              var prizePoolContractAddresses = prizePoolContractAddressesResults[prizePoolAddress];
              var prizeStrategyAddress = prizePoolContractAddresses.prizeStrategy[0];
              var prizeStategyContractAddresses = prizeStrategyContractAddressesResults[prizeStrategyAddress];
              var tokenAddress = prizePoolContractAddresses.token[0];
              var ticketAddress = prizeStategyContractAddresses.ticket[0];
              var sponsorshipAddress = prizeStategyContractAddresses.sponsorship[0];
              var token = makeToken(tokenAddress, tokenResults[tokenAddress]);
              var ticket = makeToken(ticketAddress, tokenResults[ticketAddress]);
              var sponsorship = makeToken(sponsorshipAddress, tokenResults[sponsorshipAddress]);
              var podAddress = getPodAddress(chainId, prizePoolAddress);
              var podStablecoin = podAddress ? makePodToken(podAddress, tokenResults[podAddress]) : null;
              v3PrizePools.push({
                chainId: chainId,
                addresses: {
                  prizePool: prizePoolAddress,
                  prizeStrategy: prizeStrategyAddress,
                  token: tokenAddress,
                  ticket: ticketAddress,
                  sponsorship: sponsorshipAddress,
                  pod: podAddress,
                  tokenFaucets: getPrizePoolTokenFaucetAddresses(chainId, prizePoolAddress)
                },
                tokens: {
                  token: token,
                  ticket: ticket,
                  sponsorship: sponsorship,
                  podStablecoin: podStablecoin
                }
              });
            });
            return _context3.abrupt("return", v3PrizePools);

          case 21:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function getPrizePools(_x4, _x5, _x6) {
    return _ref4.apply(this, arguments);
  };
}();
/**
 * Convert an etherplex response to a Token
 * @param tokenAddress
 * @param etherplexTokenResponse
 * @returns
 */


var makeToken = function makeToken(tokenAddress, etherplexTokenResponse) {
  return {
    address: tokenAddress,
    symbol: etherplexTokenResponse.symbol[0],
    name: etherplexTokenResponse.name[0],
    decimals: etherplexTokenResponse.decimals[0]
  };
};
/**
 * Convert an etherplex response to a PodToken
 * @param tokenAddress
 * @param etherplexTokenResponse
 * @returns
 */


var makePodToken = function makePodToken(tokenAddress, etherplexTokenResponse) {
  return {
    address: tokenAddress,
    symbol: etherplexTokenResponse.symbol[0],
    name: etherplexTokenResponse.name[0],
    decimals: etherplexTokenResponse.decimals[0],
    pricePerShare: getAmountFromUnformatted(etherplexTokenResponse.getPricePerShare[0], etherplexTokenResponse.decimals[0])
  };
};
/**
 * Finds pod data in the pod addresses constant
 * @param chainId
 * @param prizePoolAddress
 * @returns
 */


var getPodAddress = function getPodAddress(chainId, prizePoolAddress) {
  var _V3$POD_ADDRESSES, _pods$find;

  var pods = (_V3$POD_ADDRESSES = V3.POD_ADDRESSES) == null ? void 0 : _V3$POD_ADDRESSES[chainId];
  return pods ? (_pods$find = pods.find(function (pod) {
    return getAddress(pod.prizePool) === getAddress(prizePoolAddress);
  })) == null ? void 0 : _pods$find.pod : null;
};

/**
 * Fetch users balances for all tokens in all prize pools
 * @param usersAddress
 * @returns
 */

var useAllUsersV3Balances = function useAllUsersV3Balances(usersAddress) {
  var _useV3PrizePools = useV3PrizePools(),
      v3PrizePools = _useV3PrizePools.data,
      isFetched = _useV3PrizePools.isFetched;

  var chainIds = isFetched ? Object.keys(v3PrizePools).map(Number) : [];
  var providers = getReadProviders(chainIds);

  var _useCoingeckoTokenPri = useCoingeckoTokenPricesAcrossChains(getTokenAddressesFromPrizePools(v3PrizePools)),
      tokenPrices = _useCoingeckoTokenPri.data,
      isTokenPricesFetched = _useCoingeckoTokenPri.isFetched;

  return useQueries(chainIds.map(function (chainId) {
    return {
      queryKey: ['useAllUsersV3Balances', usersAddress, chainId, tokenPrices],
      queryFn: function queryFn() {
        return getUserV3BalancesByChainId(chainId, usersAddress, providers[chainId], v3PrizePools[chainId], tokenPrices);
      },
      enabled: isFetched && isTokenPricesFetched && Boolean(usersAddress)
    };
  }));
};
/**
 * Fetch balances for user for all tokens
 * @param chainId
 * @param usersAddress
 * @param provider
 * @param prizePools
 * @returns
 */

var getUserV3BalancesByChainId = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(chainId, usersAddress, provider, prizePools, tokenPrices) {
    var batchRequests, balanceOfResults, balances;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            batchRequests = []; // Fetch balances

            prizePools.forEach(function (prizePool) {
              var _prizePool$tokens = prizePool.tokens,
                  ticket = _prizePool$tokens.ticket,
                  token = _prizePool$tokens.token,
                  sponsorship = _prizePool$tokens.sponsorship,
                  podStablecoin = _prizePool$tokens.podStablecoin;
              var tokenContract = contract(token.address, ERC20Abi, token.address);
              var ticketContract = contract(ticket.address, ERC20Abi, ticket.address);
              var sponsorshipContract = contract(sponsorship.address, ERC20Abi, sponsorship.address);
              var podStablecoinContract = podStablecoin ? contract(podStablecoin.address, ERC20Abi, podStablecoin.address) : null;
              batchRequests.push(tokenContract.balanceOf(usersAddress), ticketContract.balanceOf(usersAddress), sponsorshipContract.balanceOf(usersAddress));

              if (!!podStablecoinContract) {
                batchRequests.push(podStablecoinContract.balanceOf(usersAddress));
              }
            });
            _context.next = 4;
            return batch.apply(void 0, [provider].concat(batchRequests));

          case 4:
            balanceOfResults = _context.sent;
            // Format balances, merge USD data
            balances = [];
            prizePools.forEach(function (prizePool) {
              var _tokenPrices$token$ad;

              var _prizePool$tokens2 = prizePool.tokens,
                  ticket = _prizePool$tokens2.ticket,
                  token = _prizePool$tokens2.token,
                  sponsorship = _prizePool$tokens2.sponsorship,
                  podStablecoin = _prizePool$tokens2.podStablecoin;
              var tokenUsd = tokenPrices == null ? void 0 : (_tokenPrices$token$ad = tokenPrices[token.address]) == null ? void 0 : _tokenPrices$token$ad.usd;
              var tokenWithUsdBalance = makeTokenWithUsdBalance(token, tokenUsd, balanceOfResults);
              var ticketWithUsdBalance = makeTokenWithUsdBalance(ticket, tokenUsd, balanceOfResults);
              var sponsorshipWithUsdBalance = makeTokenWithUsdBalance(sponsorship, tokenUsd, balanceOfResults);
              var podStablecoinWithUsdBalance = podStablecoin ? makePodStablecoinTokenWithUsdBalance(podStablecoin, tokenUsd, balanceOfResults) : null;
              var ticketBalance = {
                chainId: chainId,
                ticket: ticketWithUsdBalance,
                token: tokenWithUsdBalance,
                prizePool: prizePool
              };
              var sponsorshipBalance = {
                chainId: chainId,
                ticket: sponsorshipWithUsdBalance,
                token: tokenWithUsdBalance,
                prizePool: prizePool,
                isSponsorship: true
              };
              var podBalance = podStablecoinWithUsdBalance ? {
                chainId: chainId,
                ticket: podStablecoinWithUsdBalance,
                token: tokenWithUsdBalance,
                prizePool: prizePool,
                pricePerShare: podStablecoin.pricePerShare,
                isPod: true
              } : null;
              balances.push(ticketBalance, sponsorshipBalance);

              if (podBalance) {
                balances.push(podBalance);
              }
            });
            return _context.abrupt("return", {
              chainId: chainId,
              balances: balances,
              isTokenPricesFetched: Boolean(tokenPrices)
            });

          case 8:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function getUserV3BalancesByChainId(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();
/**
 * Format data. Calculate USD value of token balance.
 * @param token
 * @param usdPerToken
 * @param etherplexBalanceOfResults
 * @returns
 */


var makeTokenWithUsdBalance = function makeTokenWithUsdBalance(token, usdPerToken, etherplexBalanceOfResults) {
  var balanceUnformatted = etherplexBalanceOfResults[token.address].balanceOf[0];
  var balance = getAmountFromUnformatted(balanceUnformatted, token.decimals);
  var balanceUsdUnformatted = usdPerToken ? amountMultByUsd(balanceUnformatted, usdPerToken) : BigNumber.from(0);
  var balanceUsd = getAmountFromUnformatted(balanceUsdUnformatted, token.decimals, {
    style: 'currency',
    currency: 'USD'
  });
  var balanceUsdScaled = toScaledUsdBigNumber(balanceUsd.amount);
  return _extends({}, token, balance, {
    usdPerToken: usdPerToken,
    balanceUsd: balanceUsd,
    balanceUsdScaled: balanceUsdScaled,
    hasBalance: !balance.amountUnformatted.isZero()
  });
};
/**
 * Format data. Calculate USD value of token balance.
 * Converts balance of pod stablecoin to the amount of the underlying token then multiplies by price.
 * @param token
 * @param usdPerToken
 * @param etherplexBalanceOfResults
 * @returns
 */


var makePodStablecoinTokenWithUsdBalance = function makePodStablecoinTokenWithUsdBalance(token, usdPerToken, etherplexBalanceOfResults) {
  var balanceUnformatted = etherplexBalanceOfResults[token.address].balanceOf[0];
  var balance = getAmountFromUnformatted(balanceUnformatted, token.decimals);
  var balanceUsdUnformatted = usdPerToken ? amountMultByUsd(balanceUnformatted.mul(parseUnits('1', token.decimals)).mul(token.pricePerShare.amountUnformatted).div(parseUnits('1', token.decimals)).div(parseUnits('1', token.decimals)), usdPerToken) : BigNumber.from(0);
  var balanceUsd = getAmountFromUnformatted(balanceUsdUnformatted, token.decimals, {
    style: 'currency',
    currency: 'USD'
  });
  var balanceUsdScaled = toScaledUsdBigNumber(balanceUsd.amount);
  return _extends({}, token, balance, {
    usdPerToken: usdPerToken,
    balanceUsd: balanceUsd,
    balanceUsdScaled: balanceUsdScaled,
    hasBalance: !balance.amountUnformatted.isZero()
  });
};
/**
 * Pulls token addresses from prize pools
 * @param v3PrizePools
 * @returns
 */


var getTokenAddressesFromPrizePools = function getTokenAddressesFromPrizePools(v3PrizePools) {
  return !!v3PrizePools && Object.keys(v3PrizePools).reduce(function (tokens, chainId) {
    tokens[Number(chainId)] = v3PrizePools[Number(chainId)].map(function (prizePool) {
      return prizePool.tokens.token.address;
    });
    return tokens;
  }, {});
};

var useUsersV3PrizePoolBalance = function useUsersV3PrizePoolBalance(usersAddress, chainId, prizePoolAddress, ticketAddress) {
  var queriesResult = useAllUsersV3Balances(usersAddress);
  return useMemo(function () {
    var _queriesResult$find;

    var refetch = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                return _context.abrupt("return", queriesResult == null ? void 0 : queriesResult.forEach(function (queryResult) {
                  return queryResult.refetch();
                }));

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function refetch() {
        return _ref.apply(this, arguments);
      };
    }();

    var isFetched = queriesResult == null ? void 0 : queriesResult.every(function (queryResult) {
      return queryResult.isFetched;
    });
    var isFetching = queriesResult == null ? void 0 : queriesResult.some(function (queryResult) {
      return queryResult.isFetching;
    });
    var balances = queriesResult == null ? void 0 : (_queriesResult$find = queriesResult.find(function (qr) {
      var _qr$data, _qr$data2;

      return !!qr && (qr == null ? void 0 : qr.isFetched) && (qr == null ? void 0 : (_qr$data = qr.data) == null ? void 0 : _qr$data['chainId']) === chainId && (qr == null ? void 0 : (_qr$data2 = qr.data) == null ? void 0 : _qr$data2['balances'].some(function (b) {
        return b.prizePool.addresses.prizePool === prizePoolAddress && b.prizePool.addresses.ticket === ticketAddress;
      }));
    })) == null ? void 0 : _queriesResult$find.data['balances'].find(function (b) {
      return b.prizePool.addresses.prizePool === prizePoolAddress && b.prizePool.addresses.ticket === ticketAddress;
    });
    return {
      isFetching: isFetching,
      isFetched: isFetched,
      refetch: refetch,
      data: balances
    };
  }, [queriesResult]);
};

var usePodExitFee = function usePodExitFee(chainId, podAddress, underlyingTokenAddress, amountToWithdrawUnformatted, decimals) {
  var provider = getReadProvider(chainId);
  return useQuery(['usePodExitFee', chainId, podAddress, amountToWithdrawUnformatted == null ? void 0 : amountToWithdrawUnformatted.toString()], function () {
    return getPodExitFee(provider, podAddress, underlyingTokenAddress, amountToWithdrawUnformatted, decimals);
  }, {
    enabled: Boolean(amountToWithdrawUnformatted)
  });
};

var getPodExitFee = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(provider, podAddress, underlyingTokenAddress, amountToWithdrawUnformatted, decimals) {
    var batchCalls, podContract, underlyingTokenContract, response, _float, exitFee;

    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            batchCalls = [];
            podContract = contract(podAddress, PodAbi, podAddress);
            underlyingTokenContract = contract(underlyingTokenAddress, ERC20Abi, underlyingTokenAddress);
            batchCalls.push(podContract.getEarlyExitFee(amountToWithdrawUnformatted), underlyingTokenContract.balanceOf(podAddress));
            _context.next = 6;
            return batch.apply(void 0, [provider].concat(batchCalls));

          case 6:
            response = _context.sent;
            _float = getAmountFromUnformatted(response[underlyingTokenAddress].balanceOf[0], decimals);
            exitFee = getAmountFromUnformatted(response[podAddress].getEarlyExitFee[0], decimals);
            return _context.abrupt("return", {
              decimals: decimals,
              "float": _float,
              exitFee: exitFee
            });

          case 10:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function getPodExitFee(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

var useUsersTokenFaucetRewards = function useUsersTokenFaucetRewards(chainId, usersAddress, prizePool, tokenFaucetAddress, underlyingToken) {
  var provider = getReadProvider(chainId);

  var _useTokenFaucetData = useTokenFaucetData(chainId, tokenFaucetAddress, prizePool, underlyingToken),
      tokenFaucetData = _useTokenFaucetData.data,
      isTokenFaucetDataFetched = _useTokenFaucetData.isFetched;

  var enabled = isTokenFaucetDataFetched && !!underlyingToken;
  return useQuery(['useUsersTokenFaucetRewards', chainId, usersAddress, tokenFaucetAddress], function () {
    return getTokenFaucetRewards(provider, usersAddress, tokenFaucetAddress, tokenFaucetData);
  }, {
    enabled: enabled
  });
};

var getTokenFaucetRewards = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(provider, usersAddress, tokenFaucetAddress, tokenFaucetData) {
    var dripToken, tokenFaucetContract, tokenFaucetResults, claimableAmountUnformatted, claimableAmount;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            dripToken = tokenFaucetData.dripToken; // Fetch asset and claimable amount

            tokenFaucetContract = contract(tokenFaucetAddress, TokenFaucetAbi, tokenFaucetAddress);
            _context.next = 4;
            return batch(provider, tokenFaucetContract.claim(usersAddress));

          case 4:
            tokenFaucetResults = _context.sent;
            claimableAmountUnformatted = tokenFaucetResults[tokenFaucetAddress].claim[0];
            claimableAmount = getAmountFromUnformatted(claimableAmountUnformatted, dripToken.decimals);
            return _context.abrupt("return", _extends({}, claimableAmount, dripToken, {
              hasBalance: !claimableAmountUnformatted.isZero()
            }));

          case 8:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function getTokenFaucetRewards(_x, _x2, _x3, _x4) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * Returns a users balances for V3 Prize Pools.
 * Filters zero balances.
 * @param usersAddress
 * @param filterAdditionalPools
 * @returns
 */

var useUsersV3PrizePoolBalances = function useUsersV3PrizePoolBalances(usersAddress) {
  var queriesResult = useAllUsersV3Balances(usersAddress);
  return useMemo(function () {
    var refetch = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
        return _regeneratorRuntime().wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                return _context.abrupt("return", queriesResult == null ? void 0 : queriesResult.forEach(function (queryResult) {
                  return queryResult.refetch();
                }));

              case 1:
              case "end":
                return _context.stop();
            }
          }
        }, _callee);
      }));

      return function refetch() {
        return _ref.apply(this, arguments);
      };
    }();

    var isFetched = queriesResult == null ? void 0 : queriesResult.every(function (queryResult) {
      return queryResult.isFetched;
    });
    var isFetching = queriesResult == null ? void 0 : queriesResult.some(function (queryResult) {
      return queryResult.isFetching;
    });
    var balances = [];
    var totalValueUsdScaled = BigNumber.from(0);
    queriesResult.forEach(function (queryResult) {
      var data = queryResult.data,
          isFetched = queryResult.isFetched;

      if (isFetched && !!data) {
        data['balances'].forEach(function (balance) {
          balances.push(balance);
          totalValueUsdScaled = totalValueUsdScaled.add(balance.ticket.balanceUsdScaled);
        });
      }
    });
    var totalValueUsd = getAmountFromUnformatted(totalValueUsdScaled, '2');
    return {
      isFetching: isFetching,
      isFetched: isFetched,
      refetch: refetch,
      data: {
        balances: balances,
        totalValueUsd: totalValueUsd,
        totalValueUsdScaled: totalValueUsdScaled
      }
    };
  }, [queriesResult]);
};

function useV3ExitFee(chainId, poolAddress, ticketAddress, usersAddress, amountUnformatted) {
  var readProvider = getReadProvider(chainId);
  var enabled = Boolean(amountUnformatted && usersAddress && chainId && ticketAddress && poolAddress);
  return useQuery(['useV3ExitFee', enabled, amountUnformatted == null ? void 0 : amountUnformatted.toString(), usersAddress, chainId, ticketAddress, poolAddress], function () {
    return fetchExitFee(readProvider, usersAddress, poolAddress, ticketAddress, amountUnformatted);
  }, {
    enabled: enabled
  });
}
var fetchExitFee = /*#__PURE__*/function () {
  var _ref = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee(readProvider, usersAddress, poolAddress, ticketAddress, amountUnformatted) {
    var etherplexPrizePoolContract, values;
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.prev = 0;
            etherplexPrizePoolContract = contract('prizePool', PrizePoolAbi, poolAddress);
            _context.next = 4;
            return batch(readProvider, etherplexPrizePoolContract.calculateEarlyExitFee(usersAddress, ticketAddress, amountUnformatted));

          case 4:
            values = _context.sent;
            return _context.abrupt("return", values.prizePool.calculateEarlyExitFee.exitFee);

          case 8:
            _context.prev = 8;
            _context.t0 = _context["catch"](0);
            console.warn(_context.t0.message);
            return _context.abrupt("return", BigNumber.from(0));

          case 12:
          case "end":
            return _context.stop();
        }
      }
    }, _callee, null, [[0, 8]]);
  }));

  return function fetchExitFee(_x, _x2, _x3, _x4, _x5) {
    return _ref.apply(this, arguments);
  };
}();

/**
 * Returns a dictionary keyed by the token addresses filled with token data.
 * Stores token data in the cache.
 * @param chainId
 * @param tokenAddresses
 * @returns
 */

var useTokens = function useTokens(chainId, tokenAddresses) {
  var queryClient = useQueryClient();
  var readProvider = useReadProvider(chainId);
  var enabled = tokenAddresses.every(function (tokenAddress) {
    return !!tokenAddress && typeof tokenAddress === 'string';
  }) && Array.isArray(tokenAddresses) && tokenAddresses.length > 0 && Boolean(chainId) && !!readProvider;

  var getCacheKey = function getCacheKey(id) {
    return [QUERY_KEYS.tokens, chainId, id];
  };

  return useQuery(getCacheKey(tokenAddresses), /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getTokens(chainId, tokenAddresses, readProvider);

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({
    enabled: enabled
  }, NO_REFETCH, {
    onSuccess: function onSuccess(data) {
      return populatePerIdCache(queryClient, getCacheKey, data);
    }
  }));
};
/**
 * Returns the requested token data.
 * @param chainId
 * @param tokenAddress
 * @returns
 */

var useToken = function useToken(chainId, tokenAddress) {
  var _result$data;

  var result = useTokens(chainId, [tokenAddress]);
  return _extends({}, result, {
    data: (_result$data = result.data) == null ? void 0 : _result$data[tokenAddress]
  });
};
var getTokens = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(chainId, tokenAddresses, provider) {
    var batchCalls, response, result;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            batchCalls = [];
            tokenAddresses.map(function (tokenAddress) {
              var tokenContract = contract(tokenAddress, ERC20Abi, tokenAddress);
              batchCalls.push(tokenContract.decimals().name().symbol().totalSupply());
            });
            _context2.next = 4;
            return batch.apply(void 0, [provider].concat(batchCalls));

          case 4:
            response = _context2.sent;
            result = {};
            Object.keys(response).map(function (tokenAddress) {
              var decimals = response[tokenAddress].decimals[0];
              var name = response[tokenAddress].name[0];
              var symbol = response[tokenAddress].symbol[0];
              var totalSupplyUnformatted = response[tokenAddress].totalSupply[0];
              var totalSupply = formatUnits(totalSupplyUnformatted, decimals);
              var totalSupplyPretty = numberWithCommas(totalSupply);
              result[tokenAddress] = {
                address: tokenAddress,
                decimals: decimals,
                name: name,
                symbol: symbol,
                totalSupply: totalSupply,
                totalSupplyPretty: totalSupplyPretty,
                totalSupplyUnformatted: totalSupplyUnformatted
              };
            });
            return _context2.abrupt("return", result);

          case 8:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getTokens(_x, _x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();

var _excluded$1 = ["data"];
/**
 * Returns a dictionary keyed by the provided token addresses filled with
 * the provided spenders allowance for the provided user for the each token
 * @param chainId
 * @param usersAddress
 * @param spenderAddress
 * @param tokenAddresses
 * @returns
 */

var useTokenAllowances = function useTokenAllowances(chainId, usersAddress, spenderAddress, tokenAddresses) {
  var readProvider = getReadProvider(chainId);
  var queryClient = useQueryClient();
  var enabled = Boolean(usersAddress) && Boolean(spenderAddress) && tokenAddresses.reduce(function (aggregate, current) {
    return aggregate && Boolean(current);
  }, true) && Array.isArray(tokenAddresses) && tokenAddresses.length > 0 && Boolean(chainId);

  var getCacheKey = function getCacheKey(id) {
    return [QUERY_KEYS.tokenAllowances, chainId, usersAddress, spenderAddress, id];
  };

  return useQuery(getCacheKey(tokenAddresses), /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getTokenAllowances(readProvider, usersAddress, spenderAddress, tokenAddresses);

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({}, NO_REFETCH, {
    enabled: enabled,
    onSuccess: function onSuccess(data) {
      return populatePerIdCache(queryClient, getCacheKey, data);
    }
  }));
};
/**
 * Returns the provided spenders allowance for the provided user for the provided token
 * @param chainId
 * @param usersAddress
 * @param spenderAddress
 * @param tokenAddress
 * @returns
 */

var useTokenAllowance = function useTokenAllowance(chainId, usersAddress, spenderAddress, tokenAddress) {
  var _useTokenAllowances = useTokenAllowances(chainId, usersAddress, spenderAddress, [tokenAddress]),
      tokenAllowances = _useTokenAllowances.data,
      queryData = _objectWithoutPropertiesLoose(_useTokenAllowances, _excluded$1);

  return _extends({}, queryData, {
    data: tokenAllowances ? tokenAllowances[tokenAddress] : null
  });
};

var getTokenAllowances = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(readProvider, usersAddress, spenderAddress, tokenAddresses) {
    var batchCalls, response, result;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            batchCalls = [];
            tokenAddresses.map(function (tokenAddress) {
              var tokenContract = contract(tokenAddress, ERC20Abi, tokenAddress);
              batchCalls.push(tokenContract.allowance(usersAddress, spenderAddress));
            });
            _context2.next = 4;
            return batch.apply(void 0, [readProvider].concat(batchCalls));

          case 4:
            response = _context2.sent;
            result = {};
            Object.keys(response).map(function (tokenAddress) {
              var allowanceUnformatted = response[tokenAddress].allowance[0];
              result[tokenAddress] = allowanceUnformatted;
            });
            return _context2.abrupt("return", result);

          case 8:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getTokenAllowances(_x, _x2, _x3, _x4) {
    return _ref2.apply(this, arguments);
  };
}();

var SIMPLE_PRICES_CHAIN_ID_MAP = {
  1: 'ethereum',
  4: 'ethereum',
  5: 'ethereum',
  137: 'matic-network',
  80001: 'matic-network',
  42220: 'celo',
  44787: 'celo',
  56: 'binancecoin',
  97: 'binancecoin',
  43113: 'avalanche-2',
  43114: 'avalanche-2',
  420: 'ethereum',
  10: 'ethereum',
  42161: 'ethereum',
  421613: 'ethereum'
};
var useCoingeckoSimplePrices = function useCoingeckoSimplePrices() {
  return useQuery([QUERY_KEYS.getCoingeckoTokenPrices], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getCoingeckoSimplePrices();

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), {
    staleTime: Infinity,
    enabled: true,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false
  });
};

var getCoingeckoSimplePrices = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    var url, ids, response, tokenPrices;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            url = new URL(COINGECKO_API_URL + "/simple/price");
            ids = Array.from(new Set(Object.values(SIMPLE_PRICES_CHAIN_ID_MAP)));
            url.searchParams.set('ids', ids.join(','));
            url.searchParams.set('vs_currencies', 'usd');
            _context2.next = 7;
            return fetch(url.toString());

          case 7:
            response = _context2.sent;
            _context2.next = 10;
            return response.json();

          case 10:
            tokenPrices = _context2.sent;
            return _context2.abrupt("return", tokenPrices);

          case 14:
            _context2.prev = 14;
            _context2.t0 = _context2["catch"](0);
            console.error(_context2.t0.message);
            return _context2.abrupt("return", undefined);

          case 18:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[0, 14]]);
  }));

  return function getCoingeckoSimplePrices() {
    return _ref2.apply(this, arguments);
  };
}();

var _Object$freeze$2;
var COINGECKO_API_URL$1 = 'https://api.coingecko.com/api/v3';
var COINGECKO_ASSET_PLATFORMS$1 = /*#__PURE__*/Object.freeze((_Object$freeze$2 = {}, _Object$freeze$2[NETWORK.mainnet] = 'ethereum', _Object$freeze$2[NETWORK.bsc] = 'binance-smart-chain', _Object$freeze$2[NETWORK.polygon] = 'polygon-pos', _Object$freeze$2[NETWORK.celo] = 'celo', _Object$freeze$2[NETWORK.goerli] = 'goerli', _Object$freeze$2[NETWORK.mumbai] = 'mumbai', _Object$freeze$2[NETWORK.avalanche] = 'avalanche', _Object$freeze$2[NETWORK.optimism] = 'optimistic-ethereum', _Object$freeze$2[NETWORK['optimism-goerli']] = 'optimistic-ethereum', _Object$freeze$2));
var useCoingeckoTokenData = function useCoingeckoTokenData(chainId, contractAddress) {
  var validNetworks = Object.keys(COINGECKO_ASSET_PLATFORMS$1);
  var isValidNetwork = validNetworks.includes(chainId.toString()) && chainId !== NETWORK.goerli;
  var isValidAddress = isAddress(contractAddress);
  var assetPlatform = COINGECKO_ASSET_PLATFORMS$1[chainId];
  var enabled = Boolean(contractAddress) && isValidAddress && isValidNetwork && Boolean(chainId);
  return useQuery(['useCoingeckoTokenData', contractAddress, chainId], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getCoingeckoTokenData(assetPlatform, contractAddress);

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), {
    staleTime: Infinity,
    enabled: enabled,
    refetchInterval: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false
  });
};

var getCoingeckoTokenData = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(assetPlatform, contractAddress) {
    var response;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            _context2.next = 3;
            return fetch(COINGECKO_API_URL$1 + "/coins/" + assetPlatform + "/contract/" + contractAddress);

          case 3:
            response = _context2.sent;
            _context2.next = 6;
            return response.json();

          case 6:
            return _context2.abrupt("return", _context2.sent);

          case 9:
            _context2.prev = 9;
            _context2.t0 = _context2["catch"](0);
            console.warn(_context2.t0.message);
            return _context2.abrupt("return", undefined);

          case 13:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[0, 9]]);
  }));

  return function getCoingeckoTokenData(_x, _x2) {
    return _ref2.apply(this, arguments);
  };
}();

var useCoingeckoTokenImage = function useCoingeckoTokenImage(chainId, address) {
  var _data$image;

  var formattedAddress = address == null ? void 0 : address.toLowerCase();

  var _useCoingeckoTokenDat = useCoingeckoTokenData(chainId, formattedAddress),
      data = _useCoingeckoTokenDat.data,
      isFetched = _useCoingeckoTokenDat.isFetched,
      isFetching = _useCoingeckoTokenDat.isFetching;

  return {
    data: data == null ? void 0 : (_data$image = data.image) == null ? void 0 : _data$image.small,
    isFetched: isFetched,
    isFetching: isFetching
  };
};

var useCoingeckoExchangeRates = function useCoingeckoExchangeRates() {
  return useQuery([QUERY_KEYS.getCoingeckoExchangeRates], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return getCoingeckoExchangeRates();

          case 2:
            return _context.abrupt("return", _context.sent);

          case 3:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({
    staleTime: Infinity,
    enabled: true
  }, NO_REFETCH));
};
var getCoingeckoExchangeRates = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    var url, response, jsonResponse, exchangeRates;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.prev = 0;
            url = new URL(COINGECKO_API_URL + "/exchange_rates");
            _context2.next = 4;
            return fetch(url.toString());

          case 4:
            response = _context2.sent;
            _context2.next = 7;
            return response.json();

          case 7:
            jsonResponse = _context2.sent;
            exchangeRates = jsonResponse.rates;

            if (!exchangeRates) {
              _context2.next = 13;
              break;
            }

            return _context2.abrupt("return", exchangeRates);

          case 13:
            console.error(jsonResponse.status);
            return _context2.abrupt("return", undefined);

          case 15:
            _context2.next = 21;
            break;

          case 17:
            _context2.prev = 17;
            _context2.t0 = _context2["catch"](0);
            console.error(_context2.t0.message);
            return _context2.abrupt("return", undefined);

          case 21:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[0, 17]]);
  }));

  return function getCoingeckoExchangeRates() {
    return _ref2.apply(this, arguments);
  };
}();

var useGasCosts = function useGasCosts(chainId) {
  var enabled = Boolean(chainId);
  var refetchInterval = useRefetchInterval(chainId);
  return useQuery(['useGasCosts', chainId], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", getGasCosts(chainId));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), {
    refetchInterval: refetchInterval,
    enabled: enabled
  });
};

var getGasCosts = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(chainId) {
    var result, body;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return fetch("https://pooltogether-api.com/gas/" + chainId);

          case 2:
            result = _context2.sent;
            _context2.next = 5;
            return result.json();

          case 5:
            body = _context2.sent;
            return _context2.abrupt("return", body.result);

          case 7:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getGasCosts(_x) {
    return _ref2.apply(this, arguments);
  };
}();

// to calculate how much gas will probably cost in both USD and the native currency

var useGasCostEstimate = function useGasCostEstimate(gasAmount, chainId) {
  var _useCoingeckoSimplePr = useCoingeckoSimplePrices(),
      prices = _useCoingeckoSimplePr.data,
      pricesIsFetched = _useCoingeckoSimplePr.isFetched,
      pricesError = _useCoingeckoSimplePr.error;

  var _useGasCosts = useGasCosts(chainId),
      gasCosts = _useGasCosts.data,
      gasCostsIsFetched = _useGasCosts.isFetched,
      gasCostsError = _useGasCosts.error;

  var isFetched = pricesIsFetched && gasCostsIsFetched;
  var error = gasCostsError || pricesError;
  var totalGasUsd, totalGasWei;

  if (gasCosts && Boolean(prices) && isFetched && !error) {
    totalGasWei = calculateTotalGasWei(gasCosts, gasAmount);
    totalGasUsd = calculateTotalGasUsd(prices, chainId, totalGasWei);
  }

  return {
    totalGasWei: totalGasWei,
    totalGasUsd: totalGasUsd,
    isFetched: isFetched,
    error: error
  };
};

var calculateTotalGasUsd = function calculateTotalGasUsd(prices, chainId, totalGasWei) {
  var usd = prices[SIMPLE_PRICES_CHAIN_ID_MAP[chainId]].usd;
  return totalGasWei.mul(Math.round(usd * 100).toString()).div(100);
};

var calculateTotalGasWei = function calculateTotalGasWei(gasCosts, gasAmount) {
  var standardGasCostGwei = Number(gasCosts.ProposeGasPrice); // Convert gwei to wei

  var standardGasCostWei = BigNumber.from(Math.round(standardGasCostGwei * 1000)).mul(parseUnits('1', 9)).div(1000);
  return gasAmount.mul(standardGasCostWei);
};

var MerkleDistributorAbi = [{
  inputs: [{
    internalType: 'address',
    name: 'token_',
    type: 'address'
  }, {
    internalType: 'bytes32',
    name: 'merkleRoot_',
    type: 'bytes32'
  }],
  stateMutability: 'nonpayable',
  type: 'constructor'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'index',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'address',
    name: 'account',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }],
  name: 'Claimed',
  type: 'event'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'index',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'account',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'amount',
    type: 'uint256'
  }, {
    internalType: 'bytes32[]',
    name: 'merkleProof',
    type: 'bytes32[]'
  }],
  name: 'claim',
  outputs: [],
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  inputs: [{
    internalType: 'uint256',
    name: 'index',
    type: 'uint256'
  }],
  name: 'isClaimed',
  outputs: [{
    internalType: 'bool',
    name: '',
    type: 'bool'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'merkleRoot',
  outputs: [{
    internalType: 'bytes32',
    name: '',
    type: 'bytes32'
  }],
  stateMutability: 'view',
  type: 'function'
}, {
  inputs: [],
  name: 'token',
  outputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  stateMutability: 'view',
  type: 'function'
}];

var useRetroactivePoolClaimData = function useRetroactivePoolClaimData(address) {
  var _useFetchRetroactiveP = useFetchRetroactivePoolClaimData(address),
      refetch = _useFetchRetroactiveP.refetch,
      data = _useFetchRetroactiveP.data,
      isFetching = _useFetchRetroactiveP.isFetching,
      isFetched = _useFetchRetroactiveP.isFetched,
      error = _useFetchRetroactiveP.error;

  return {
    loading: !isFetched,
    refetch: refetch,
    data: data,
    isFetching: isFetching,
    isFetched: isFetched,
    error: error
  };
};

var useFetchRetroactivePoolClaimData = function useFetchRetroactivePoolClaimData(usersAddress) {
  var chainId = useGovernanceChainId();
  var readProvider = getReadProvider(chainId);
  return useQuery([QUERY_KEYS.retroactivePoolClaimDataQuery, usersAddress, chainId], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", getRetroactivePoolClaimData(readProvider, chainId, usersAddress));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), {
    enabled: Boolean(usersAddress) && isAddress(usersAddress),
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false
  });
};

var getRetroactivePoolClaimData = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(provider, chainId, usersAddress) {
    var checksummedAddress, merkleDistributionData, amount, formattedAmount, isClaimed;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            checksummedAddress = ethers.utils.getAddress(usersAddress);
            merkleDistributionData = {};
            _context2.prev = 2;
            _context2.next = 5;
            return getMerkleDistributionData(checksummedAddress, chainId);

          case 5:
            merkleDistributionData = _context2.sent;
            _context2.next = 11;
            break;

          case 8:
            _context2.prev = 8;
            _context2.t0 = _context2["catch"](2);
            return _context2.abrupt("return", {
              isMissing: true,
              isClaimed: false,
              amount: ethers.BigNumber.from(0),
              formattedAmount: 0
            });

          case 11:
            amount = ethers.BigNumber.from(merkleDistributionData.amount).toString();
            formattedAmount = Number(ethers.utils.formatUnits(amount, 18));
            _context2.next = 15;
            return getIsClaimed(provider, chainId, merkleDistributionData.index);

          case 15:
            isClaimed = _context2.sent;
            return _context2.abrupt("return", _extends({}, merkleDistributionData, {
              amount: amount,
              formattedAmount: formattedAmount,
              isClaimed: isClaimed
            }));

          case 17:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2, null, [[2, 8]]);
  }));

  return function getRetroactivePoolClaimData(_x, _x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();

var getMerkleDistributionData = /*#__PURE__*/function () {
  var _ref3 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee3(usersAddress, chainId) {
    var response;
    return _regeneratorRuntime().wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            _context3.next = 2;
            return fetch("https://merkle.pooltogether.com/.netlify/functions/merkleAddressData?address=" + usersAddress + (chainId === 4 ? '&chainId=4&testVersion=v4' : ''));

          case 2:
            response = _context3.sent;

            if (!(response.status === 200)) {
              _context3.next = 9;
              break;
            }

            _context3.next = 6;
            return response.json();

          case 6:
            return _context3.abrupt("return", _context3.sent);

          case 9:
            throw new Error('User does not exist in Merkle tree or 500 error');

          case 10:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function getMerkleDistributionData(_x4, _x5) {
    return _ref3.apply(this, arguments);
  };
}();

var getIsClaimed = /*#__PURE__*/function () {
  var _ref4 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee4(provider, chainId, index) {
    var merkleDistributorContract, _yield$batch, merkleDistributor;

    return _regeneratorRuntime().wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            merkleDistributorContract = contract('merkleDistributor', MerkleDistributorAbi, GOVERNANCE_CONTRACT_ADDRESSES[chainId].MerkleDistributor);
            _context4.next = 3;
            return batch(provider, merkleDistributorContract.isClaimed(index));

          case 3:
            _yield$batch = _context4.sent;
            merkleDistributor = _yield$batch.merkleDistributor;
            return _context4.abrupt("return", merkleDistributor.isClaimed[0]);

          case 6:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function getIsClaimed(_x6, _x7, _x8) {
    return _ref4.apply(this, arguments);
  };
}();

var getPrizePoolTokensQueryKey = function getPrizePoolTokensQueryKey(prizePool) {
  return ['prizePoolTokens', prizePool == null ? void 0 : prizePool.id()];
};
var usePrizePoolTokens = function usePrizePoolTokens(prizePool) {
  return useQuery(getPrizePoolTokensQueryKey(prizePool), /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", getPrizePoolTokens(prizePool));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({}, NO_REFETCH));
};
var getPrizePoolTokens = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(prizePool) {
    var ticketDataPromise, tokenDataPromise, _yield$Promise$all, ticketData, tokenData, ticketContract, tokenContract, ticket, token;

    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            ticketDataPromise = prizePool.getTicketData();
            tokenDataPromise = prizePool.getTokenData();
            _context2.next = 4;
            return Promise.all([ticketDataPromise, tokenDataPromise]);

          case 4:
            _yield$Promise$all = _context2.sent;
            ticketData = _yield$Promise$all[0];
            tokenData = _yield$Promise$all[1];
            _context2.next = 9;
            return prizePool.getTicketContract();

          case 9:
            ticketContract = _context2.sent;
            _context2.next = 12;
            return prizePool.getTokenContract();

          case 12:
            tokenContract = _context2.sent;
            ticket = {
              address: ticketContract.address,
              symbol: ticketData.symbol,
              name: ticketData.name,
              decimals: ticketData.decimals
            };
            token = {
              address: tokenContract.address,
              symbol: tokenData.symbol,
              name: tokenData.name,
              decimals: tokenData.decimals
            };
            return _context2.abrupt("return", {
              prizePoolId: prizePool.id(),
              ticket: ticket,
              token: token
            });

          case 16:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getPrizePoolTokens(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var getUsersPrizePoolBalancesQueryKey = function getUsersPrizePoolBalancesQueryKey(usersAddress, prizePool) {
  return ['useUsersPrizePoolBalances', prizePool == null ? void 0 : prizePool.id(), usersAddress];
};
var useUsersPrizePoolBalances = function useUsersPrizePoolBalances(usersAddress, prizePool) {
  var refetchInterval = useRefetchInterval(prizePool == null ? void 0 : prizePool.chainId);

  var _usePrizePoolTokens = usePrizePoolTokens(prizePool),
      tokens = _usePrizePoolTokens.data,
      isFetched = _usePrizePoolTokens.isFetched;

  var enabled = Boolean(prizePool) && Boolean(usersAddress) && isFetched;
  return useQuery(getUsersPrizePoolBalancesQueryKey(usersAddress, prizePool), /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", getUsersPrizePoolBalances(prizePool, usersAddress, tokens));

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), {
    refetchInterval: refetchInterval,
    enabled: enabled
  });
};
var getUsersPrizePoolBalances = /*#__PURE__*/function () {
  var _ref2 = /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2(prizePool, usersAddress, tokens) {
    var balances, ticket, token;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return prizePool.getUsersPrizePoolBalances(usersAddress);

          case 2:
            balances = _context2.sent;
            ticket = tokens.ticket, token = tokens.token;
            return _context2.abrupt("return", {
              prizePool: prizePool,
              usersAddress: usersAddress,
              balances: {
                ticket: _extends({}, ticket, {
                  hasBalance: !balances.ticket.isZero(),
                  amountUnformatted: balances.ticket,
                  amount: formatUnits(balances.ticket, ticket.decimals),
                  amountPretty: formatUnformattedBigNumberForDisplay(balances.ticket, ticket.decimals)
                }),
                token: _extends({}, token, {
                  hasBalance: !balances.token.isZero(),
                  amountUnformatted: balances.token,
                  amount: formatUnits(balances.token, token.decimals),
                  amountPretty: formatUnformattedBigNumberForDisplay(balances.token, token.decimals)
                })
              }
            });

          case 5:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getUsersPrizePoolBalances(_x, _x2, _x3) {
    return _ref2.apply(this, arguments);
  };
}();

var GovernorAlphaAbi = [{
  inputs: [{
    internalType: 'address',
    name: 'timelock_',
    type: 'address'
  }, {
    internalType: 'address',
    name: 'uni_',
    type: 'address'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'constructor'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'id',
    type: 'uint256'
  }],
  name: 'ProposalCanceled',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'id',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'address',
    name: 'proposer',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'address[]',
    name: 'targets',
    type: 'address[]'
  }, {
    indexed: false,
    internalType: 'uint256[]',
    name: 'values',
    type: 'uint256[]'
  }, {
    indexed: false,
    internalType: 'string[]',
    name: 'signatures',
    type: 'string[]'
  }, {
    indexed: false,
    internalType: 'bytes[]',
    name: 'calldatas',
    type: 'bytes[]'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'startBlock',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'endBlock',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'string',
    name: 'description',
    type: 'string'
  }],
  name: 'ProposalCreated',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'id',
    type: 'uint256'
  }],
  name: 'ProposalExecuted',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'uint256',
    name: 'id',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'eta',
    type: 'uint256'
  }],
  name: 'ProposalQueued',
  type: 'event'
}, {
  anonymous: false,
  inputs: [{
    indexed: false,
    internalType: 'address',
    name: 'voter',
    type: 'address'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }, {
    indexed: false,
    internalType: 'bool',
    name: 'support',
    type: 'bool'
  }, {
    indexed: false,
    internalType: 'uint256',
    name: 'votes',
    type: 'uint256'
  }],
  name: 'VoteCast',
  type: 'event'
}, {
  constant: true,
  inputs: [],
  name: 'BALLOT_TYPEHASH',
  outputs: [{
    internalType: 'bytes32',
    name: '',
    type: 'bytes32'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'DOMAIN_TYPEHASH',
  outputs: [{
    internalType: 'bytes32',
    name: '',
    type: 'bytes32'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }],
  name: 'cancel',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }, {
    internalType: 'bool',
    name: 'support',
    type: 'bool'
  }],
  name: 'castVote',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }, {
    internalType: 'bool',
    name: 'support',
    type: 'bool'
  }, {
    internalType: 'uint8',
    name: 'v',
    type: 'uint8'
  }, {
    internalType: 'bytes32',
    name: 'r',
    type: 'bytes32'
  }, {
    internalType: 'bytes32',
    name: 's',
    type: 'bytes32'
  }],
  name: 'castVoteBySig',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }],
  name: 'execute',
  outputs: [],
  payable: true,
  stateMutability: 'payable',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }],
  name: 'getActions',
  outputs: [{
    internalType: 'address[]',
    name: 'targets',
    type: 'address[]'
  }, {
    internalType: 'uint256[]',
    name: 'values',
    type: 'uint256[]'
  }, {
    internalType: 'string[]',
    name: 'signatures',
    type: 'string[]'
  }, {
    internalType: 'bytes[]',
    name: 'calldatas',
    type: 'bytes[]'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'voter',
    type: 'address'
  }],
  name: 'getReceipt',
  outputs: [{
    components: [{
      internalType: 'bool',
      name: 'hasVoted',
      type: 'bool'
    }, {
      internalType: 'bool',
      name: 'support',
      type: 'bool'
    }, {
      internalType: 'uint96',
      name: 'votes',
      type: 'uint96'
    }],
    internalType: 'struct GovernorAlpha.Receipt',
    name: '',
    type: 'tuple'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'address',
    name: '',
    type: 'address'
  }],
  name: 'latestProposalIds',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'name',
  outputs: [{
    internalType: 'string',
    name: '',
    type: 'string'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'proposalCount',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'proposalMaxOperations',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'proposalThreshold',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  name: 'proposals',
  outputs: [{
    internalType: 'uint256',
    name: 'id',
    type: 'uint256'
  }, {
    internalType: 'address',
    name: 'proposer',
    type: 'address'
  }, {
    internalType: 'uint256',
    name: 'eta',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'startBlock',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'endBlock',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'forVotes',
    type: 'uint256'
  }, {
    internalType: 'uint256',
    name: 'againstVotes',
    type: 'uint256'
  }, {
    internalType: 'bool',
    name: 'canceled',
    type: 'bool'
  }, {
    internalType: 'bool',
    name: 'executed',
    type: 'bool'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'address[]',
    name: 'targets',
    type: 'address[]'
  }, {
    internalType: 'uint256[]',
    name: 'values',
    type: 'uint256[]'
  }, {
    internalType: 'string[]',
    name: 'signatures',
    type: 'string[]'
  }, {
    internalType: 'bytes[]',
    name: 'calldatas',
    type: 'bytes[]'
  }, {
    internalType: 'string',
    name: 'description',
    type: 'string'
  }],
  name: 'propose',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: false,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }],
  name: 'queue',
  outputs: [],
  payable: false,
  stateMutability: 'nonpayable',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'quorumVotes',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [{
    internalType: 'uint256',
    name: 'proposalId',
    type: 'uint256'
  }],
  name: 'state',
  outputs: [{
    internalType: 'enum GovernorAlpha.ProposalState',
    name: '',
    type: 'uint8'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'timelock',
  outputs: [{
    internalType: 'contract TimelockInterface',
    name: '',
    type: 'address'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'uni',
  outputs: [{
    internalType: 'contract UniInterface',
    name: '',
    type: 'address'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'votingDelay',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}, {
  constant: true,
  inputs: [],
  name: 'votingPeriod',
  outputs: [{
    internalType: 'uint256',
    name: '',
    type: 'uint256'
  }],
  payable: false,
  stateMutability: 'view',
  type: 'function'
}];

var _templateObject;
var GOVERNOR_ALPHA = '0xB3a87172F555ae2a2AB79Be60B336D2F7D0187f0';
var GOVERNANCE_SUBGRAPH = 'https://api.thegraph.com/subgraphs/name/pooltogether/pooltogether-governance';
function useAllProposals() {
  return useQuery(['useAllProposals', NETWORK.mainnet], /*#__PURE__*/_asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee() {
    return _regeneratorRuntime().wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            return _context.abrupt("return", getProposals());

          case 1:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  })), _extends({}, NO_REFETCH));
}

function getProposals() {
  return _getProposals.apply(this, arguments);
}

function _getProposals() {
  _getProposals = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime().mark(function _callee2() {
    var chainId, provider, query, proposals, subgraphData, batchCalls, proposalChainData;
    return _regeneratorRuntime().wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            chainId = NETWORK.mainnet;
            provider = getReadProvider(chainId);
            query = proposalsQuery();
            proposals = {};
            _context2.next = 6;
            return request(GOVERNANCE_SUBGRAPH, query);

          case 6:
            subgraphData = _context2.sent;
            batchCalls = [];
            subgraphData.proposals.forEach(function (proposal) {
              var governanceContract = contract(proposal.id, GovernorAlphaAbi, GOVERNOR_ALPHA);
              batchCalls.push(governanceContract.proposals(proposal.id));
              batchCalls.push(governanceContract.state(proposal.id));
            });
            _context2.next = 11;
            return batch.apply(void 0, [provider].concat(batchCalls));

          case 11:
            proposalChainData = _context2.sent;
            subgraphData.proposals.forEach(function (proposal) {
              var id = proposal.id,
                  description = proposal.description;
              proposals[id] = {
                id: id,
                title: (description == null ? void 0 : description.split(/# |\n/g)[1]) || 'Untitled',
                description: description || 'No description.',
                againstVotes: proposalChainData[id].proposals.againstVotes,
                forVotes: proposalChainData[id].proposals.forVotes,
                totalVotes: proposalChainData[id].proposals.forVotes.add(proposalChainData[id].proposals.againstVotes),
                status: PROPOSAL_STATES[proposalChainData[id].state[0]]
              };
            });
            return _context2.abrupt("return", proposals);

          case 14:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));
  return _getProposals.apply(this, arguments);
}

var proposalsQuery = function proposalsQuery() {
  return gql(_templateObject || (_templateObject = _taggedTemplateLiteralLoose(["\n    query proposalsQuery {\n      proposals {\n        id\n        proposer {\n          id\n          delegatedVotesRaw\n          delegatedVotes\n          tokenHoldersRepresentedAmount\n        }\n        targets\n        values\n        signatures\n        calldatas\n        startBlock\n        endBlock\n        description\n        status\n        executionETA\n      }\n    }\n  "])));
};

var _excluded$2 = ["data", "isFetched", "error"];
function useAllProposalsByStatus() {
  var _useAllProposals = useAllProposals(),
      proposals = _useAllProposals.data,
      isFetched = _useAllProposals.isFetched,
      error = _useAllProposals.error,
      useQueryResults = _objectWithoutPropertiesLoose(_useAllProposals, _excluded$2);

  var data = useMemo(function () {
    var _sortedProposals;

    var sortedProposals = (_sortedProposals = {}, _sortedProposals[ProposalStatus.pending] = [], _sortedProposals[ProposalStatus.active] = [], _sortedProposals[ProposalStatus.cancelled] = [], _sortedProposals[ProposalStatus.defeated] = [], _sortedProposals[ProposalStatus.succeeded] = [], _sortedProposals[ProposalStatus.queued] = [], _sortedProposals[ProposalStatus.expired] = [], _sortedProposals[ProposalStatus.executed] = [], _sortedProposals);
    if (!proposals || !isFetched || error) return null;
    Object.values(proposals).forEach(function (proposal) {
      sortedProposals[proposal.status].push(proposal);
    });
    return sortedProposals;
  }, [proposals, isFetched, error]);
  return _extends({}, useQueryResults, {
    isFetched: isFetched,
    error: error,
    data: data
  });
}

export { APP_ENVIRONMENTS, COINGECKO_API_URL, COINGECKO_ASSET_PLATFORMS, NO_REFETCH, PROPOSAL_STATES, ProposalStatus, QUERY_KEYS, fetchExitFee, getAppEnvString, getCoingeckoExchangeRates, getCoingeckoTokenPrices, getLPPrizePoolMetadata, getPrizePoolTokens, getPrizePoolTokensQueryKey, getRefetchInterval, getStoredIsTestnetsCookie, getTokens, getUsersPrizePoolBalances, getUsersPrizePoolBalancesQueryKey, useAllProposals, useAllProposalsByStatus, useAllUsersV3Balances, useCoingeckoExchangeRates, useCoingeckoSimplePrices, useCoingeckoTokenData, useCoingeckoTokenImage, useCoingeckoTokenPrices, useCoingeckoTokenPricesAcrossChains, useCookieOptions, useGasCostEstimate, useGasCosts, useGovernanceChainId, useInitCookieOptions, useInitTheGraphApiKey, useIsTestnets, useLPTokenUsdValue, usePodExitFee, usePrizePoolTokens, useRefetchInterval, useRetroactivePoolClaimData, useToken, useTokenAllowance, useTokenAllowances, useTokenBalance, useTokenBalances, useTokenFaucetData, useTokens, useUsersPrizePoolBalances, useUsersTokenFaucetRewards, useUsersV3PrizePoolBalance, useUsersV3PrizePoolBalances, useV3ChainIds, useV3ExitFee, useV3PrizePools };
//# sourceMappingURL=hooks.esm.js.map
